import { EventEmitter, Inject, Injectable, OnDestroy, Output } from '@angular/core';
import { encode,decode } from "@msgpack/msgpack";
import { CacheService } from '../cache/cache-service';

import 'reflect-metadata';
import { Observable, Subject, timer } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { jsonObject, jsonMember, jsonArrayMember, jsonMapMember, TypedJSON } from 'typedjson';
import { cacheTTLSeconds, cacheEnabled } from '../misc-service'
import { XProjectClientSettings, XPROJECTORCLIENTSETTINGS } from './xprojector-client-settings-service';
import { LOGGERSERVICE, XprojLoggerService } from '../logger/xproj-logger-service';

export interface PromiseResolve {
	resolve : (response ?: Object) => void;
	reject : (reason ?: any) => void;
};

export function MsgPackCloneObject(data : Object) : Object
{
  return decode(encode(data));
}

// This one is just marked for export level because of crap reasons right now
export class XProjectorLowlevelClient
{
  currentMessageLength : number = 0;
  currentMessageBuffer : Uint8Array;
  currentMessageFetchedBytes : number = 0;
  requestid : number = 0;

  private token : string = '';
  private clientRemotenodeid : string = null;

  MessageHeaderBuffer;
  MessageHeaderBytesFetched;

  constructor(private settings : XProjectClientSettings,
    @Inject(LOGGERSERVICE) private logger: XprojLoggerService) {
    this.MessageHeaderBuffer = new Uint8Array(4);
    this.MessageHeaderBytesFetched = 0;
  }

  public socket : WebSocket = null;
  public isConnected:boolean = false;
  public isAuthenticated:boolean = false;
  public SocketState : string = "NOT CONNECTED";

  public OnAuthenticated : () => void;
  public OnDisconnected : () => void;
  public OnMessage : (Data : Object) => void;

  public SetHost(host : string) {
    if (this.settings) {
      this.settings.host = host;
    }
  }

  public GetNextMessageID(): number{
    return this.requestid++;
  }

  public async SendMessageIgnoreID(data : Object, ignoreTargetRemoteId? : boolean)
  {
    if(!ignoreTargetRemoteId)
    {
      if(this.clientRemotenodeid?.length > 0)
      {
        this.logger.debug("Sending data to target remotenodeid:", this.clientRemotenodeid, data);
        data["targetnodeid"] = this.clientRemotenodeid;
      }
    }

    this.logger.debug("SendMessageIgnoreID", this.clientRemotenodeid);
    const encoded: Uint8Array = encode(data);

    let lenarr = new ArrayBuffer(4 + encoded.byteLength);
    let lenview = new DataView(lenarr);
    lenview.setUint32(0, encoded.byteLength, true);
    //this.logger.info("sending msgpack with length " + encoded.byteLength.toString());


    for(let i = 0; i < encoded.byteLength; i++)
    {
      lenview.setUint8(i+4, encoded[i] );
    }

    this.socket.send(lenarr);
  }

  public async SendMessage(data : Object)
  {
    data["messageid"] = this.GetNextMessageID();
    if(this.clientRemotenodeid?.length > 0)
    {
      this.logger.debug("Sending data to target remotenodeid:", this.clientRemotenodeid);
      data["targetnodeid"] = this.clientRemotenodeid;
    }
    this.SendMessageIgnoreID(data);
  }

  SendLoginInformation()
  {
    let message = {
      request: "authenticate",
      token: this.token
    }
    message["messageid"] = this.GetNextMessageID();
    this.SendMessageIgnoreID(message, true /*ignore remote node id */);
  }

  async HandleOnConnect()
  {
    this.SocketState = "OPEN";
    this.logger.info("on connect");
    this.SendLoginInformation();
    this.isConnected = true;
    this.isAuthenticated = false;
    this.logger.info('OPEN');
  }

  async HandleOnClose()
  {
    this.SocketState = "CLOSED";
    this.isConnected = false;
    this.isAuthenticated = false;
    this.OnDisconnected();
    this.logger.info('CLOSED ONCLOSE');
  }

  async HandleOnError(ev : Event)
  {
    this.SocketState = "CLOSED";
    this.isConnected = false;
    this.isAuthenticated = false;
    this.OnDisconnected();

    this.logger.info('CLOSED ERROR');
  }

  HandleAuthenticationMessage(data : Object)
  {
    this.logger.info("Got authentication answer:");
    if(data["method"] != "authenticate")
    {
      this.logger.info("Incorrect response in authentication");
      this.socket.close();
      return;
    }
    if(data["success"] != true)
    {
      this.logger.info("Could not log in");
      this.socket.close();
      return;
    }

    this.logger.info("Authenticated!");
    this.isAuthenticated = true;
    this.OnAuthenticated();
  }

  HandleMessage(data : Object)
  {
    //  isAuthenticated
    if(!this.isAuthenticated)
    {
      this.HandleAuthenticationMessage(data);
      return;
    }

    //this.logger.info('HANDLE MESSAGE', data);

    this.OnMessage(data);
  }


  DataReceived( data: number) : void
  {
    if(this.MessageHeaderBytesFetched < this.MessageHeaderBuffer.byteLength)
    {
      this.MessageHeaderBuffer[this.MessageHeaderBytesFetched++] = data;

      if(this.MessageHeaderBytesFetched < this.MessageHeaderBuffer.byteLength)
      return;

      let view = new DataView(this.MessageHeaderBuffer.buffer);
      this.currentMessageLength = view.getUint32(0, true);
      //this.logger.info("starting to receive message with lenght size of " + this.currentMessageLength.toString());
      this.currentMessageBuffer = new Uint8Array(this.currentMessageLength);

      return;
    }

    this.currentMessageBuffer[this.currentMessageFetchedBytes++] = data;

    if(this.currentMessageFetchedBytes < this.currentMessageLength)
    return;

    try
    {
      let NewMessage = decode(this.currentMessageBuffer);
      //this.logger.info("completed receving message with lenght size of " +
      //this.currentMessageLength.toString() +
      //" unpacked size: " + JSON.stringify(NewMessage).length );
      this.HandleMessage(NewMessage);
    }
    catch(err)
    {
      this.logger.info("error in messagepack parsing", err)
    }

    this.currentMessageFetchedBytes = 0;
    this.currentMessageBuffer = null;
    this.currentMessageLength = 0;
    this.MessageHeaderBytesFetched = 0;
  }

  HandleOnMessage(message : MessageEvent) : void
  {
    let view = new DataView(message.data);
    for(let i = 0; i < message.data.byteLength; i++)
    {
      this.DataReceived( view.getUint8(i) );
    }
  }


  public SetActiveRemoteNodeID(remotenodeid: string)
  {
    this.logger.info("Setting target remotenodeid:", this.clientRemotenodeid);
    this.clientRemotenodeid = remotenodeid;
  }

  public async Connect(token : string = '')
  {

    this.Disconnect();
    if (token?.length > 0) {
      this.token = token;
    }

    this.logger.info('Connect()', this.token);

    this.isAuthenticated =false;
    this.isConnected = false;
    this.socket = new WebSocket(this.settings.host);
    this.socket.binaryType = 'arraybuffer';

    this.socket.onopen = this.HandleOnConnect.bind(this);
    this.socket.onmessage = this.HandleOnMessage.bind(this);
    this.socket.onclose = this.HandleOnClose.bind(this);
    this.socket.onerror = this.HandleOnError.bind(this);
  }


  public Disconnect()
  {
    if (this.socket) {
      this.socket.close();
    }
  }
}

// Enums
export enum XDataType
{
  Float32 = 0,
  Float64 = 1,
  UInt8 = 2,
  Int32 = 3,
  Int64 = 4,
  Number = 5,
  Timestamp = 6,
  String = 7,
  UInt128=8
}

export enum FilterComparator
{
    Equals = 0,
    NotEquals = 1,
    LessThan = 2,
    LessThanOrEquals = 3,
    GreaterThan = 4,
    GreatherThanOrEquals = 5,
    BitAnd = 6,
    BitExlusiveOr = 7
};

export enum FilterLogicalGroupType
{
    AND = 0,
    OR = 1
};

export enum RelativeTimestampOrigo
{
    NOW                         = 0,
    NOWUTC                      = 1,
    EXACT                       = 2,
    BEGINNING_OF_THIS_YEAR      = 3,
    BEGINNING_OF_THIS_MONTH     = 4,
    BEGINNING_OF_THIS_DAY       = 5,
    BEGINNING_OF_THIS_HOUR      = 6,
    BEGINNING_OF_THIS_MINUTE    = 7,
    BEGINNING_OF_THIS_YEARUTC   = 8,
    BEGINNING_OF_THIS_MONTHUTC  = 9,
    BEGINNING_OF_THIS_DAYUTC    = 10,
    BEGINNING_OF_THIS_HOURUTC   = 11,
    BEGINNING_OF_THIS_MINUTEUTC = 12

};

export enum OffsetType
{
    YEAR      = 0,
    MONTH     = 1,
    DAY       = 2,
    HOUR      = 3,
    MINUTE    = 4,
    SECOND    = 5
};

export enum DownSampleMethod
{
    Average = 0,
    NearestNeighbour =1,
    Max = 2,
    Min = 3
};

export enum DFTMethod
{
  AUTO                      = 0,
  DFT_GAP_FILL_ZERO         = 1,
  DFTP_GAP_REPEAT           = 2,
  DFT_GAP_INTERPOLATE       = 3,
  DFT_NON_UNIFORM           = 4
};

export enum Transformation
{
    NONE                                = 0,

    TIMESTAMP_RESOLUTION_YEAR           = 1,
    TIMESTAMP_RESOLUTION_MONTH          = 2,
    TIMESTAMP_RESOLUTION_DAY            = 3,
    TIMESTAMP_RESOLUTION_HOUR           = 4,
    TIMESTAMP_RESOLUTION_MINUTE         = 5,
    TIMESTAMP_RESOLUTION_SECOND         = 6,

    TIMESTAMP_DAYOFYEAR                 = 7,
    TIMESTAMP_DAYOFMONTH                = 8,
    TIMESTAMP_DAYOFWEEK                 = 9,
    TIMESTAMP_SECONDS_OF_DAY            = 10,
    TIMESTAMP_MINUTES_OF_DAY            = 11,
    TIMESTAMP_HOURS                     = 12,
    TIMESTAMP_MINUTES                   = 13,
    TIMESTAMP_SECONDS                   = 14,

    FIXED_BINSIZE                       = 15,

    FORWARD_DIFF                        = 16,

    ABSOLUTE_VALUE                      = 18
};

export enum Aggregation
{
    // None is actually a non-useable agg func (not like none in transforms)
    NONE                = 0,
    COUNT               = 1,
    SUM                 = 2,
    MAX                 = 3,
    MIN                 = 4,
    FIRST               = 5,
    LAST                = 6,
    CHECKSUM            = 7,
    FORWARD_DIFF        = 8,
    DISTINCT_COUNT      = 10,

    MEAN_ARITHMETIC     = 20,
    MEAN_GEOMETRIC      = 21,
    MEAN_HARMONIC       = 22,

    MEDIAN              = 40,

    SD_SAMPLED_UNCORRECTED          = 50,
    SD_SAMPLED_CORRECTED            = 51,
    SD_SAMPLED_UNBIASED_APPROX_ND   = 52,

    MAD_ORIGO_MEAN_ARITHMETIC    = 70,
    MAD_ORIGO_MEAN_GEOMETRIC     = 71,
    MAD_ORIGO_MEAN_HARMONIC      = 72,

    MAD_ORIGO_MEDIAN    = 80,

    VARIANCE_ND         = 100,
    S2_ND               = 101
};

// Communications classes

@jsonObject
export class ColumnFiltering
{
  @jsonMember
  queryfilterid : number = 0;

  @jsonMember
  comparator : FilterComparator;

  @jsonMember
  columnname : string = '';
}

@jsonObject
export class ColumnFilteringNumerical extends ColumnFiltering
{
  @jsonMember
  value : number;
}

@jsonObject
export class ColumnFilteringString extends ColumnFiltering
{
  @jsonMember
  value : string;
}
@jsonObject
export class ColumnFilteringTimestamp extends ColumnFiltering
{
  @jsonMember
  value : Date;
}

@jsonObject
export class ColumnFilteringRelativeTimestamp extends ColumnFiltering
{
  @jsonMember
  origo : RelativeTimestampOrigo = RelativeTimestampOrigo.NOW;

  @jsonMember
  exactorigo : Date;

  @jsonMember
  offsetyears : number = 0;

  @jsonMember
  offsetmonths : number = 0;

  @jsonMember
  offsetdays : number = 0;

  @jsonMember
  offsethours : number = 0;

  @jsonMember
  offsetminutes : number = 0;

  @jsonMember
  offsetseconds : number = 0;

}

@jsonObject
export class FilterLogicalGroup
{
    @jsonMember
    public queryfilterid : number = 0;

    @jsonMember
    public type : FilterLogicalGroupType = FilterLogicalGroupType.AND;

    @jsonArrayMember(Number)
    public filters : Array<number> = new Array<number>();
}


@jsonObject
export class ColumnSortingDescription
{
  @jsonMember
  public columnname : string = "";

  @jsonMember
  public descending:boolean =false;
}

@jsonObject
export class ColumnGroupingDescription
{
  @jsonMember
  public columnname : string = '';

  @jsonMember
  public columnoutname: string = '';

  @jsonMember
  public columntransformation : Transformation =Transformation.NONE;

  @jsonMember
  public parameter : number = 0.0;
}

@jsonObject
export class BaseQueryInputColumnDescription
{
  @jsonMember
  public columnname : string = '';

  @jsonMember
  public columnoutname: string = '';

  @jsonMember
  public columnaggregation : Aggregation;

  @jsonMember
  public columntransformation : Transformation = Transformation.NONE;

}

@jsonObject
export class LuaExportColumn {
  @jsonMember
  public columnname : string = '';

  @jsonMember
  public scriptname : string = '';
}

@jsonObject
export class LuaQueryColumn {
  @jsonArrayMember(LuaExportColumn)
  public inputcolumns : LuaExportColumn[] = [];

  @jsonMember
  public datatypeout : XDataType = XDataType.String;

  @jsonMember
  public columnoutname : string = '';

  @jsonMember
  public luascript : string = '';
}


@jsonObject
export class BaseQuery
{
  @jsonMember
  public targetprojectionid : string;

  @jsonArrayMember(String)
  public targetgroup: Array<string> = new Array<string>();

  @jsonArrayMember(BaseQueryInputColumnDescription)
  public columns: Array<BaseQueryInputColumnDescription> = new Array<BaseQueryInputColumnDescription>();

  @jsonArrayMember(LuaQueryColumn)
  public scriptedcolumnspostaggregation : LuaQueryColumn[] = [];

  @jsonMember
  public filter : FilterLogicalGroup = new FilterLogicalGroup();

  @jsonArrayMember(FilterLogicalGroup)
  public subfiltergroups : Array<FilterLogicalGroup> = new Array<FilterLogicalGroup> ();

  @jsonArrayMember(ColumnFilteringString)
  public stringfilters : Array<ColumnFilteringString> = new Array<ColumnFilteringString>();

  @jsonArrayMember(ColumnFilteringTimestamp)
  public timestampfilters : Array<ColumnFilteringTimestamp> = new Array<ColumnFilteringTimestamp>();

  @jsonArrayMember(ColumnFilteringRelativeTimestamp)
  public relativetimestampfilters : Array<ColumnFilteringRelativeTimestamp> = new Array<ColumnFilteringRelativeTimestamp>();

  @jsonArrayMember(ColumnFilteringNumerical)
  public numericalfilters : Array<ColumnFilteringNumerical> = new Array<ColumnFilteringNumerical>();

  @jsonMember
  public sorting : ColumnSortingDescription = new ColumnSortingDescription();

  @jsonMember
  public grouping : ColumnGroupingDescription = new ColumnGroupingDescription();

  @jsonMember
  public seekoffset : number = 0;

  @jsonMember
  public maxitems : number = 0;

  public Clone() : BaseQuery {
	  return TypedJSON.parse(TypedJSON.stringify(this, BaseQuery), BaseQuery);
  }


}

export class BaseQueryOutputColumnDescription
{
  public columnname : string;
  public columnoutname : string;
  public ColumnTransformation : number;
  public datatype : XDataType;
  public primarykey : boolean;
  public writable : boolean;
  public indexintypedvector : number;
}

export class BaseQueryResult
{
  public nrpoints : number;
  public nroriginalpoints : number;
  public columns : Array<BaseQueryOutputColumnDescription>;
  public datanumbers : Array<Array<number>>;
  public datatimestamps : Array<Array<Date>>;
  public datastrings : Array<Array<string>>;
}

export class TextSearchQuery
{
    public targetprojectionid: string = "";
    public targetgroup: Array<string> = new Array<string>();
    public columnname: string = ""; // If empty : Search all columns
    public searchvalue : string = "";
    public seekoffset: number = 0;
    public maxitems : number = 100;
}

export class TextSearchQueryResult
{
    public strings : Array<string>;
    public nrpoints : number;
    public nroriginalpoints : number;
}


export class DownSampleQuery
{
    public query  :BaseQuery = new BaseQuery();
    public method : DownSampleMethod = DownSampleMethod.Average;
}



export enum MultiSeriesQueryType
{
    DIMENSIONAL_JOIN = 0,
    FUZZY      = 100
};

export enum MultiSeriesFuzzyMatchType
{
    NUMTIME_CLOSEST                     = 0,
    NUMTIME_SORT_SWEEP                  = 10,
    NUMTIME_SORT_ALIGN_START_THEN_SWEEP = 20,
    NUMTIME_SORT_VERIFY_SIGN_SWEEP      = 30,

    STRING_MATCH_START_CASE_SENSITIVE       = 1000,
    STRING_MATCH_START_CASE_INSENSITIVE     = 1001,
    STRING_MATCH_END_CASE_SENSITIVE         = 1002,
    STRING_MATCH_END_CASE_INSENSITIVE       = 1003,
    STRING_MATCH_CONTAINS_CASE_SENSITIVE    = 1004,
    STRING_MATCH_CONTAINS_CASE_INSENSITIVE  = 1005
};

export class MultiseriesQuery
{
    public type : MultiSeriesQueryType = MultiSeriesQueryType.DIMENSIONAL_JOIN;
    public fuzzytype : MultiSeriesFuzzyMatchType = MultiSeriesFuzzyMatchType.NUMTIME_CLOSEST;
    public queries : BaseQuery[] = [];
    public sorting : ColumnSortingDescription = new ColumnSortingDescription();
    public seekoffset: number = 0;
    public maxitems : number = 100;
    public joinablecolumns : string[] = [];
    public scriptedcolumnspostjoin : LuaQueryColumn[] = [];
}

export class DFTQuery
{
    public query  :BaseQuery = new BaseQuery();
    public method : DFTMethod = DFTMethod.AUTO;
}


export class SPCQueryDistributionConfig
{
    public mean : number = 0.0;
    public stddev : number = 0.0;
};

export enum SPCChartType
{
    // Distribution specific
    XBAR        = 0,
    SIGMA       = 1,
    INDIVIDUALS = 2,
    P           = 3,
    NP          = 4,
    RANGE       = 5,
    EWMA        = 6
    // Distribution free
};

export class SPCQuery
{
    type : SPCChartType = SPCChartType.XBAR;
    distribution : SPCQueryDistributionConfig;
    query : BaseQuery;
    nelsonsignal1_enabled : boolean = false;
    nelsonsignal2_enabled : boolean = false;
    nelsonsignal3_enabled : boolean = false;
    nelsonsignal4_enabled : boolean = false;
    nelsonsignal5_enabled : boolean = false;
    nelsonsignal6_enabled : boolean = false;
    nelsonsignal7_enabled : boolean = false;
    nelsonsignal8_enabled : boolean = false;
    seekoffset : number = 0;
    maxitems: number = 1000;

    public constructor()
    {
    }
}

export class GroupsQuery
{
    public targetprojectionid : string;
    public joinedmatch : string = "";
    public groups : Array<string> = new Array<string>();
    public seekoffset : number = 0;
    public maxitems : number = 0;
}

export class GroupResult
{
    public group : Array<string>;
    public hasdata : boolean;
    public haschildren : boolean;
};

export class GroupsQueryResult
{
    public groups : Array<GroupResult>;
    public nrgroups : number;
    public nroriginalgroups : number;
};

export class SetDataColumnDescription {
  public columnname : string;
  public datatype : XDataType;
  public indexintypedvector : number;
}

export class SetDataQuery {
  public projectionid : string;
  public columns : Array<SetDataColumnDescription> = new Array<SetDataColumnDescription>();
  public datanumbers : Array<Array<number>>;
  public datatimestamps : Array<Array<Date>>;
  public datastrings : Array<Array<string>>

}

export class SetSplittedDataQuery {
  public projectionid : string;
  public group: string[] = [];
  public columns : Array<SetDataColumnDescription> = new Array<SetDataColumnDescription>();
  public datanumbers : Array<Array<number>>;
  public datatimestamps : Array<Array<Date>>;
  public datastrings : Array<Array<string>>

}

export class ClientSideIterativeQueryLevel
{
  public Query: Array<BaseQuery> = new Array<BaseQuery>();
}

export class ClientSideIterativeQuery
{

}

@jsonObject
export class ProjectionColumnDescription
{
  @jsonMember
  public projectioncolumndescriptionid : string = "";

  @jsonMember
  public createdwhenutc : Date = new Date();

  @jsonMember
  public lastmodifiedutc : Date= new Date();

  @jsonMember
  public projectionid : string = "";

  @jsonMember
  public columnname : string = "";

  @jsonMember
  public scriptname : string = "";

  @jsonMember
  public primarykey : boolean = false ;

  @jsonMember
  public datatype : XDataType = XDataType.Number;

  @jsonMember
  public unit : string = "";

  @jsonMember
  public writable : boolean = false;

  @jsonMember
  public luapostprocessingscript : string = "";

  @jsonArrayMember(String)
  public group: Array<string> = new Array<string>();

  @jsonArrayMember(String)
  public tags: Array<string> = new Array<string>();

  //@jsonMapMember(String, String)
  public externalkeys : Map<string,string> = new Map<string,string>();

  @jsonMember
  public luascriptorder : number = 0;

}

export class ProjectionColumnGroupColumnDescription
{
  public projectioncolumndescriptionid : string;
  public transformluascript : string;
}

export class ProjectionColumnGrouping
{
  public projectioncolumngroupid : string;
  public groupcolumns : Array<ProjectionColumnGroupColumnDescription> = new Array<ProjectionColumnGroupColumnDescription>();
  public outputcolumns : Array<ProjectionColumnDescription>  = new  Array<ProjectionColumnDescription>();
  public transformcolumndescriptionluascript: string ="";
}

export class ProjectionDependencyProjection
{
  public projectionid : string;
  public scriptname : string;
}

export class ProjectionDependencyRemote
{
  public remotenodeid : string;
  public scriptname : string;
}

export class ProjectionDependencies
{
    public globalluascriptids : Array<string> = new Array<string>();
    public datasourceids : Array<string> = new Array<string>();
    public projections : Array<ProjectionDependencyProjection> = new Array<ProjectionDependencyProjection> ();
    public remotes : Array<ProjectionDependencyProjection> = new Array<ProjectionDependencyProjection> ();
};

export enum ProjectionExecutionEntry
{
  DataSourceQuery     = 0,
  ProjectionCursor    = 1,
  LuaMainFunction     = 2
}
export class ProjectionExecutionDataSourceQuery
{
  public projectionexecutiondatasourcequeryid : string = "";
  public projectionexecutionid : string = "";
  public entrypointdatasourceid : string = "";
  public entrypointdatasourcequery : string = "";
  public modifiedatfieldname : string = "";
  public deletedfieldname : string = "";
}

export class ProjectionExecutionLuaMainFunction
{
  public projectionexecutionluamainfunctionid : string = "";
  public projectionexecutionid : string = "";
  public luascript : string = "";
}

export class ProjectionExecution
{
  public projectionexecutionid : string ="";
  public projectionid : string = "";
  public isdefaultexecution : boolean = false;
  public entrypoint : ProjectionExecutionEntry;

  public grouping : ProjectionColumnGrouping = new ProjectionColumnGrouping();
}


export class ProjectionExecutionLog
{
  public projectionexecutionlogid: string;
  public projectionid : string;
  public startedutc : Date;
  public finishedutc : Date;
  public beganwaitingfordatasourcesutc : Date;
  public finishedwaitingfordatasourcesutc: Date;
  public finishedwaitingforprojectionsutc : Date;
  public succesful: boolean;
  public log : string;
}

export class ProjectionScheduling
{
    public minintervalseconds : number = 60*5;
    public autocollect : boolean = false;
    public maxrows : number = 0;
}

export class Projection
{
  public projectionid : string;
  public parentprojectionid : string ="";
  public schedulingqueue : string = "";
  public projectiondatapath : string = "";
  public enabled : boolean;
  public createdwhenutc : Date;
  public lastmodifiedutc : Date;
  public lastprojectedutc : Date;
  public name : string;
  public prescriptlua : string = "";
  public postscriptlua : string = "";
  public description : string;
  public dependencies : ProjectionDependencies = new ProjectionDependencies();
  public tags: Array<string> = new Array<string>();
  public externalkeys : Map<string,string> = new Map<string,string>();
  //public execution: ProjectionExecution = new ProjectionExecution();
  public scheduling : ProjectionScheduling = new ProjectionScheduling();
}

export enum DataSourceDriver
{
  Broken      = 0,
  MongoDB     = 1,
  SQLServer   = 2,
  Oracle      = 3,
  PostgreSQL  = 4,
  MySQL       = 5,
  SQLite      = 6,
}


export class DataSource
{
  public datasourceid : string;
  public createdwhenutc : Date = new Date();
  public lastmodifiedutc : Date = new Date();
  public name : string = "";
  public enabled: boolean = true;
  public driver: DataSourceDriver = DataSourceDriver.Broken;
  public maxconcurrentqueries : number = 1;
  public maxconcurrentprojectionjobs : number = 1;
  public writeable : boolean = false;
  public tags: Array<string> = new Array<string>();
  public externalkeys : Map<string,string> = new Map<string,string>();
}

export class DataSourceSQLServer
{
  public datasourceid : string;
  public datasourcesqlserverid : string;
  public createdwhenutc : Date = new Date();
  public lastmodifiedutc : Date = new Date();
  public databasename : string ="";
  public hostname : string = "";
  public port : number = 1433;
  public username : string = "";
  public password : string = "";
  public serverencoding : string = "";
}

export class DataSourcePostgreSQL
{
  public datasourceid : string;
  public datasourcepostgresqlid : string;
  public createdwhenutc : Date = new Date();
  public lastmodifiedutc : Date = new Date();
  public databasename : string ="";
  public hostname : string = "";
  public port : number = 5432;
  public username : string = "";
  public password : string = "";
}

export class DataSourceMySQL
{
  public datasourceid : string;
  public datasourcemysqlid : string;
  public createdwhenutc : Date = new Date();
  public lastmodifiedutc : Date = new Date();
  public databasename : string ="";
  public hostname : string = "";
  public port : number = 5432;
  public username : string = "";
  public password : string = "";
}


export class DataSourceSQLite
{
  public datasourceid : string;
  public datasourcesqliteid : string;
  public createdwhenutc : Date = new Date();
  public lastmodifiedutc : Date = new Date();
  public databasename : string ="";
}

export class DataSourceMongoDB
{
  public datasourceid : string;
  public datasourcemongodbid : string;
  public createdwhenutc : Date = new Date();
  public lastmodifiedutc : Date = new Date();
  public databasename : string ="";
  public hostname : string = "";
  public port : number = 27017;
  public username : string = "";
  public password : string = "";
}

export class DataSourceProbeQuery
{
  public targetdatasourceid : string;
  public query : string;
}

export class DataSourceProbeQueryColumnDescription
{
  public columnname : string;
  public datatype : XDataType;
  public indexintypedvector : number;
}

export class DataSourceProbeQueryResult
{
  public nrpoints : number =0;
  public columns : Array<DataSourceProbeQueryColumnDescription> = new Array<DataSourceProbeQueryColumnDescription>();
  public datanumbers : Array<Array<number>>;
  public datatimestamps : Array<Array<Date>>;
  public datastrings : Array<Array<string>>;
}

export class ResetProjectionDataQuery
{
  public projectionid : string;
  public group : string[]
}

export class VersionResponse
{
  public branch : string;
  public commithash : string;
  public uncommittedchanges : boolean;
  public commitdate : Date;
  public builddate : Date;
}

export class Blob
{
  public id: string;
  public data : Uint8Array; // Does this work with messagepack?
  public hash : string;
}

export class SetBlob
{
  public id: string;
  public data : Uint8Array; // Does this work with messagepack?
  public previous_hash : string;
}

// Debugger

export class LuaDebuggerCommandAttach
{
  public sid: string;
}

export class LuaDebuggerCommandRelease
{
  public sid: string;
}
export class LuaDebuggerCommandRun
{
  public sid: string;
}


export class LuaDebuggerCommandStepInto
{
  public sid: string;
}


export class LuaDebuggerCommandStepOver
{
  public sid: string;
}


export class LuaDebuggerCommandSetBreakpoints
{
  public sid: string;
  public lines: number[];
}


export class LuaDebuggerCommandGetValues
{
  public sid: string;
  public instances: string[];
}

export class LuaDebuggerCommandGetValuesResponse
{
  public sid: string;
  public values: Map<string, string>;
}

export class LuaDebuggerCommandGetSource
{
  public sid: string;
}

export class LuaDebuggerCommandGetSourceResponse
{
  public sid: string;
  public sourcehash: number;
  public source: string;
}

export class LuaDebuggerCommandGetStatus
{
  public sid: string;
}

export enum LuaDebuggerState
{
    NotRunning  = 0,
    Running     = 10,
    Paused      = 20,
    Aborted     = 200
};

export class LuaDebuggerCommandGetStatusResponse
{
  public sid: string;
  public state: LuaDebuggerState;
  public pausedatsourcehash : number;
  public pausedatline : number;
}

// XAUTOMATION
export class XAUTO_XAutoProcessGraph
{
  public xautoprocessgraphid : string = "";
  public name : string = "";
  public graphxml : string = "";
  public svg : string = "";
  public svgbindings : string = "";
  public xautogroup : string = "";
  public xgroup : string = "";
  public description : string = "";
  public lastmodified : Date = new Date();
  public numericalmapxautovariables :  Array<XAUTO_PLCVariableNumericalMapXAutoVariable> = new Array<XAUTO_PLCVariableNumericalMapXAutoVariable>();
};

export class XAUTO_XAutoProcess3DGraph
{
  public xautoprocess3dgraphid : string = "";
  public name : string = "";
  public ifc : string = "";
  public fcstd : string = "";
  public babylon : string = "";
  public xautogroup : string = "";
  public xgroup : string = "";
  public description : string = "";
  public numericalmapxautovariables :  Array<XAUTO_PLCVariableNumericalMapXAutoVariable> = new Array<XAUTO_PLCVariableNumericalMapXAutoVariable>();
  public lastmodified : Date = new Date();
}


@jsonObject
export class XAUTO_PLCVariableNumericalMapXAutoVariable
{
    @jsonMember
    public scriptname : string = "";
    @jsonMember
    public description: string = "";

    // These must resolve to a 1:1 identifier
    @jsonMember
    public xautovariableid : string = ""; // either by id
    // or by combination of the three entries below
    @jsonMember
    public xname : string = "";
    @jsonMember
    public xautogroup : string = "";
    @jsonMember
    public xgroup : string= "";

   // public type : XDataType =XDataType.Float32;
   // public writable: boolean = false;

    @jsonMember
    flushiferror : boolean = false;
    @jsonMember
    flushfromblock : boolean = false;
};

export enum XAUTO_XAutoProgramType
{
    LUA                            = 0,
    //BLOCK                          = 10,
};

@jsonObject
export class XAUTO_XAutoProgram
{
  @jsonMember
  public type : XAUTO_XAutoProgramType;
  @jsonMember
  public xautoprogramid : string = "";
  @jsonMember
  public xautogroup : string = "";
  @jsonMember
  public xgroup : string = "";
  @jsonMember
  public name : string = "";
  @jsonMember
  public description : string = "";

  @jsonMember
  public lastmodifiedutc : Date = new Date();
  @jsonMember
  public laststartedutc : Date = new Date(1970,1,1);
  @jsonMember
  public lastfinishedutc : Date = new Date(1970,1,1);
  @jsonMember
  public lasterrorutc : Date = new Date(1970,1,1);
  @jsonMember
  public lasterror : string = "";

  @jsonMember
  public enabled : boolean = false;

  @jsonArrayMember(XAUTO_PLCVariableNumericalMapXAutoVariable)
  public numericalmapxautovariables : Array<XAUTO_PLCVariableNumericalMapXAutoVariable> = new Array<XAUTO_PLCVariableNumericalMapXAutoVariable>();
};

@jsonObject
export class XAUTO_XAutoProgramLua
{
  @jsonMember
  public xautoprogramid : string = "";
  @jsonMember
  public luascript_oninit : string = "";
  @jsonMember
  public luascript_onerror : string ="";
  @jsonMember
  public luascript_loop : string = "";
};

export enum XAUTO_XAutoProgramSchedulingType
{
    Interval = 0
};

@jsonObject
export class XAUTO_XAutoProgramScheduling
{
    public xautoprogramschedulingid : string = "";
    public name : string = "";
    public description: string = "";
    public type : XAUTO_XAutoProgramSchedulingType = XAUTO_XAutoProgramSchedulingType.Interval;
    public xautoprogramid : string = "";
    public xautogroup : string = "";
    public xgroup : string = "";
    public enabled : boolean = false;
}

@jsonObject
export class XAUTO_XAutoProgramSchedulingInterval
{
    public xautoprogramschedulingid : string = "";
    public hertz : number = 10;
    public laststartedutc : Date;
}

export enum XAUTO_DriverType
{
    EXTERNAL    = 0,
    REDIS       = 10,
    BACNET      = 90,
    MODBUS      = 100,
    MBUS        = 120,
    OPCUA       = 130,
    PROFIBUS    = 200,
    CANBUS      = 220,
    MQTT        = 300,
    LORAWAN_MQTT = 399,
    GPIO        = 400,
    COLLECTD    = 500,
    MIDI        = 600
};

export enum XAUTO_DriverMainErrors
{
  BADCONNECTION           = 1,
  BADAUTHENTICATION       = 2,
  BADDATA                 = 3,
  BADCONFIG               = 4
};

export class XAutoVariableTransformation
{
  // line equation: y = kx + m
  // when write, it is inverse so that y_flush = (y - m)/k
  public UseLineTransform : boolean = false;
  public LineTransform_K : number = 1;
  public LineTransform_M : number = 0;
  public LuaIn : string = "";
  public LuaOut : string = "";
};


export class XAUTO_XAutoVariableTransformation
{
  public uselinetransform : boolean = false;
  public linetransform_k : number = 1;
  public linetransform_m : number = 0;
  public luain : string = "";
  public luaout : string  = "";
};

export class XAUTO_XAutoVariable
{
  public xautodriverid : string = "";
  public xautovariableid: string = "";
  public xautogroup : string  = "";
  public xgroup : string  = "";
  public name : string  = "";
  public description : string = "";
  public defaultscriptname : string = "";
  public unit : string = "";
  public enabled: boolean = false;
  public systemlocked : boolean  = false;
  public type : XDataType = XDataType.Float64;
  public lastsampledutc : Date;
  public lastflushedutc : Date;
  public lasterrorutc : Date;
  public sampleintervalseconds : Number = 0;
  public flushintervalseconds : Number = 0;
  public writable : boolean = false;
  public transform: XAUTO_XAutoVariableTransformation = new XAUTO_XAutoVariableTransformation();  
  public defaultvalue : number = 0;
  public haslimits : boolean = false;  
  public minvalue : number = Number.MIN_VALUE;  
  public maxvalue : number = Number.MAX_VALUE;
  public usedefaultsampleintervalseconds : boolean = false;
  public usedefaultflushintervalseconds : boolean = false;
};

export class XAUTO_XAutoDriver
{
  public xautodriverid : string;
  public enabled: boolean =false;
  public systemlocked: boolean = false;
  public driver: XAUTO_DriverType;
  public lasterror : XAUTO_DriverMainErrors;

  public name : string = "";
  public description : string = "";
  public defaultxautogroup : string = "";
  public defaultxgroup : string = "";

  public lastactiveutc : Date;
  public lastconnectedutc : Date;
  public lasterrorutc : Date;
};

export enum XAUTO_ModbusType
{
  TCPIP_MASTER       = 0,
  TCPIP_SLAVE       = 1,
  // UDP_MASTER        = 2,
  // UDP_SLAVE         = 3,

  RTU_MASTER        = 10,
  RTU_SLAVE         = 11
};

export enum XAUTO_ModbusRTUMode
{
    RS485       = 0,
    RS232       = 1
};

export enum XAUTO_ModbusRTUParity
{
    None    = 0,
    Even    = 1,
    Odd     = 2
};


export class XAUTO_XAutoDriverModbus
{
  public xautodriverid : string;
  public type : XAUTO_ModbusType;
  public host : string;
  public port : number;
  public slave : number;

  public rtudevice : string;
  public rtubaud : number;
  public rtuparity : XAUTO_ModbusRTUParity;
  public rtudatabit : number;
  public rtustopbit : number;
  public rtumode : XAUTO_ModbusRTUMode;
}

export enum XAUTO_ModbusVariableType
{
  Bits        = 0,

  Int8        = 10,
  UInt8       = 11,

  Int16       = 20,
  UInt16      = 21,

  Int32       = 30,
  UInt32      = 31,

  Int64       = 40,
  UInt64      = 41,

  Float32     = 50,
  Float64     = 51,
};

export enum XAUTO_ModbusVariableByteOrder
{
  LEFT_TO_RIGHT   = 0,
  RIGHT_TO_LEFT   = 1,
};

export enum XAUTO_ModbusVariableRegisterOrder
{
  LEFT_TO_RIGHT   = 0,
  RIGHT_TO_LEFT   = 1,
};

export class XAUTO_XAutoVariableModbus
{
  public xautodriverid : string = "";
  public xautovariableid : string = "";
  public variabletype : XAUTO_ModbusVariableType = XAUTO_ModbusVariableType.Int16;
  public variableregisterorder : XAUTO_ModbusVariableRegisterOrder = XAUTO_ModbusVariableRegisterOrder.LEFT_TO_RIGHT;
  public variablebyteorder : XAUTO_ModbusVariableByteOrder = XAUTO_ModbusVariableByteOrder.LEFT_TO_RIGHT;
  public input : boolean = false;
  public address : number = 0;
  public size : number = 1;
  public bitsoffset : number = 0;
};

export class XAUTO_XAutoDriverGpio
{
    public xautodriverid : string = "";
    public chipsetname : string = "";
};

export enum XAUTO_XAutoVariableGpioMode
{
    AnalogueIn    = 0,
    DigitalIn     = 1,
    PulseCount    = 2,
    Frequency     = 3,

    AnalogueOut   = 20,
    DigitalOut    = 21
};

export enum XAutoVariableGpioAdressingMode
{
    FileDescriptor  = 0,
    ChipsLineOffset = 1
};


export class XAUTO_XAutoVariableGpio
{
    public xautodriverid : string;
    public xautovariableid : string;
    public mode : XAUTO_XAutoVariableGpioMode;
    public filename : string = "";
    public lineoffset : number;
    public voltagelow : number;
    public voltagehigh : number;
    public maxvoltage : number;
    public minvoltage : number;
    public normalized : boolean;
    public nrdecimals : number = 0;
    public commaseparator : string = ".";
};

export class XAUTO_XAutoDriverMidi
{
  public xautodriverid : string;
  public portname: string = "";
  public portnumber : number = -1;
}

export class XAUTO_XAutoVariableMidi
{
  public xautodriverid : string;
  public xautovariableid : string;
  public statusbyte : number;
  public controllerid: number;
  public continuousramp : boolean;
  public continuousrampupdatabytevalue: number;
  public continuousrampdowndatabytevalue : number;
  public continuousrampgain : number;
  public mirrorvaluetocontroller : boolean;
}


export enum XAUTO_MbusType
{
  TCPIP_MASTER=0,
  SERIAL_MASTER=10,
  SERIAL_MASTER_TO_GATEWAY=20
};

export class XAUTO_XAutoDriverMbus
{
  public xautodriverid : string;
  public tcphost: string = "";
  public tcpport : number = -1;
  public serialdevice: string = "";
  public serialbaud : number = -1;
  public type : XAUTO_MbusType;

}

export class XAUTO_XAutoVariableMbus
{
  public xautodriverid : string;
  public xautovariableid : string;

  public serialbaud : number = 0;
  public addresssecondaryid : number = 0;
  public manufacturer : string = "";
  public firmwareversion : number = 0;
  public medium : number = 0;
}

export class XAUTO_XAutoDriverCollectd
{
  public xautodriverid : string;
  public unixsocketpath : string;
};

export class XAUTO_XAutoVariableCollectd
{
  public xautodriverid : string;
  public xautovariableid : string;
  public identifier : string;
}

export class XAUTO_XAutoDriverOpcua
{
  public xautodriverid : string;
  public uri : string;
  public username : string;
  public password : string;
};
export enum XAUTO_OPCUANODEIDTYPE
{
    INT32      = 0,
    GUID       = 1,
    STRING     = 2,
    BASE64     = 3
};

export class XAUTO_XAutoVariableOpcua
{
  public xautodriverid : string;
  public xautovariableid : string;
  public nodeidtype : XAUTO_OPCUANODEIDTYPE;
  public nodeidstring : string;
  public nodeidguid : string;
  public nodeidint32 : number;
  public nodeidbase64 : string;
}

export enum XAUTO_MQTTDataFormat
{
    JSON            = 0,
    BINARY      = 1
};

export enum XAUTO_JSONType
{
  BOOL    = 0,
  DOUBLE  = 1,
  INT64   = 2,
  UINT64  = 3,
  STRING  = 4,
  OBJECT  = 5
};

export enum XAUTO_MQTTBinaryPayloadEncoding
{
    RAW            = 0,
    BASE64         = 1,
    HEX            = 2
};
export enum XAUTO_MQTTVariableEncoding
{
    JSON            = 0,
    CAYENNELPP      = 1,
    CUSTOM_LUA      = 2
};


export class XAUTO_XAutoDriverMqtt
{
    public xautodriverid : string = "";
    public uri: string = "";
    public clientid : string = "";
    public username : string;
    public password : string;
    public validateservercertificate : boolean = true;

    // example: application/1/device/0018b20000020feb/event/up
    // topic in: application/1/device/+/event/up
    public subscribetopicin : string;
    // example: application/1/device/{IDENTIFIER}/event/up
    public extractidentifierfromtopicin : string;
    // https://stackoverflow.com/questions/11627440/regex-c-extract-substring
    public topicout : string; // Keywords: {IDENTIFIER}

    public jsonpointer : string; // Example: /data
    // https://www.boost.org/doc/libs/1_86_0/libs/json/doc/html/json/dom/nested_access.html

    //public format : XAUTO_MQTTDataFormat = XAUTO_MQTTDataFormat.JSON;
    //public payloaddataencoding  : XAUTO_MQTTPayloadEncoding = XAUTO_MQTTPayloadEncoding.BASE64;
};

export class XAUTO_XAutoVariableMqtt
{
    public xautodriverid: string = "";
    public xautovariableid : string = "";

    public format : XAUTO_MQTTDataFormat = XAUTO_MQTTDataFormat.JSON;
    public encoding : XAUTO_MQTTBinaryPayloadEncoding =XAUTO_MQTTBinaryPayloadEncoding.RAW;

    public datatypein : XAUTO_JSONType = XAUTO_JSONType.DOUBLE;
    public datatypeout : XAUTO_JSONType = XAUTO_JSONType.DOUBLE;

    public mqttidentifier : string = "";

    public jsonpointerin : string = "";
    public jsonpointerout : string = "";

    public luascriptin : string = "";
    public luascriptout : string = "";

    public luascripttransformdatain : string = "";
    public luascripttransformdataout : string = "";
};


export class XAUTO_XAutoDriverExternal
{
    public xautodriverid:string = "";
};

export class XAUTO_XAutoVariableExternal
{
  public xautodriverid : string ="";
  public xautovariableid : string ="";
};

export class XAUTO_XAutoDriverRedis
{
  public xautodriverid : string = "";
};

export class XAUTO_XAutoVariableRedis
{
  public xautodriverid : string ="";
  public xautovariableid : string = ""
};

export class XAUTO_XAutoDriverBacnet
{
  public xautodriverid : string;
  public deviceinstance : number = 123;
  public objectname : string = "XAUTO Generic";
  public enableautodiscover : boolean = false;
  public enableautoprovisioning : boolean = false;
  public manuallyprobeipaddresses : Array<string> = new Array<string> ();
};


export enum XAUTO_BACNET_OBJECT_TYPE {
  OBJECT_ANALOG_INPUT = 0,
  OBJECT_ANALOG_OUTPUT = 1,
  OBJECT_ANALOG_VALUE = 2,
  OBJECT_BINARY_INPUT = 3,
  OBJECT_BINARY_OUTPUT = 4,
  OBJECT_BINARY_VALUE = 5,
  OBJECT_CALENDAR = 6,
  OBJECT_COMMAND = 7,
  OBJECT_DEVICE = 8,
  OBJECT_EVENT_ENROLLMENT = 9,
  OBJECT_FILE = 10,
  OBJECT_GROUP = 11,
  OBJECT_LOOP = 12,
  OBJECT_MULTI_STATE_INPUT = 13,
  OBJECT_MULTI_STATE_OUTPUT = 14,
  OBJECT_NOTIFICATION_CLASS = 15,
  OBJECT_PROGRAM = 16,
  OBJECT_SCHEDULE = 17,
  OBJECT_AVERAGING = 18,
  OBJECT_MULTI_STATE_VALUE = 19,
  OBJECT_TRENDLOG = 20,
  OBJECT_LIFE_SAFETY_POINT = 21,
  OBJECT_LIFE_SAFETY_ZONE = 22,
  OBJECT_ACCUMULATOR = 23,
  OBJECT_PULSE_CONVERTER = 24,
  OBJECT_EVENT_LOG = 25,
  OBJECT_GLOBAL_GROUP = 26,
  OBJECT_TREND_LOG_MULTIPLE = 27,
  OBJECT_LOAD_CONTROL = 28,
  OBJECT_STRUCTURED_VIEW = 29,
  OBJECT_ACCESS_DOOR = 30,
  OBJECT_TIMER = 31,
  /* Note: 31 was lighting output, but BACnet editor changed it... */
  OBJECT_ACCESS_CREDENTIAL = 32, /* Addendum 2008-j */
  OBJECT_ACCESS_POINT = 33,
  OBJECT_ACCESS_RIGHTS = 34,
  OBJECT_ACCESS_USER = 35,
  OBJECT_ACCESS_ZONE = 36,
  OBJECT_CREDENTIAL_DATA_INPUT = 37, /* authentication-factor-input */
  OBJECT_NETWORK_SECURITY = 38, /* Addendum 2008-g */
  OBJECT_BITSTRING_VALUE = 39, /* Addendum 2008-w */
  OBJECT_CHARACTERSTRING_VALUE = 40, /* Addendum 2008-w */
  OBJECT_DATE_PATTERN_VALUE = 41, /* Addendum 2008-w */
  OBJECT_DATE_VALUE = 42, /* Addendum 2008-w */
  OBJECT_DATETIME_PATTERN_VALUE = 43, /* Addendum 2008-w */
  OBJECT_DATETIME_VALUE = 44, /* Addendum 2008-w */
  OBJECT_INTEGER_VALUE = 45, /* Addendum 2008-w */
  OBJECT_LARGE_ANALOG_VALUE = 46, /* Addendum 2008-w */
  OBJECT_OCTETSTRING_VALUE = 47, /* Addendum 2008-w */
  OBJECT_POSITIVE_INTEGER_VALUE = 48, /* Addendum 2008-w */
  OBJECT_TIME_PATTERN_VALUE = 49, /* Addendum 2008-w */
  OBJECT_TIME_VALUE = 50, /* Addendum 2008-w */
  OBJECT_NOTIFICATION_FORWARDER = 51, /* Addendum 2010-af */
  OBJECT_ALERT_ENROLLMENT = 52, /* Addendum 2010-af */
  OBJECT_CHANNEL = 53, /* Addendum 2010-aa */
  OBJECT_LIGHTING_OUTPUT = 54, /* Addendum 2010-i */
  OBJECT_BINARY_LIGHTING_OUTPUT = 55, /* Addendum 135-2012az */
  OBJECT_NETWORK_PORT = 56, /* Addendum 135-2012az */
  OBJECT_ELEVATOR_GROUP = 57, /* Addendum 135-2012aq */
  OBJECT_ESCALATOR = 58, /* Addendum 135-2012aq */
  OBJECT_LIFT = 59, /* Addendum 135-2012aq */
  OBJECT_STAGING = 60, /* Addendum 135-2016bd */
  OBJECT_AUDIT_LOG = 61, /* Addendum 135-2016bi */
  OBJECT_AUDIT_REPORTER = 62, /* Addendum 135-2016bi */
  OBJECT_COLOR = 63, /* Addendum 135-2020ca */
  OBJECT_COLOR_TEMPERATURE = 64, /* Addendum 135-2020ca */
  BACNET_OBJECT_TYPE_LAST = OBJECT_COLOR_TEMPERATURE,
  BACNET_OBJECT_TYPE_RESERVED_MAX = 127,
  /* Enumerated values 0-127 are reserved for definition by ASHRAE. */
  /* Enumerated values 128-1023 may be used by others subject to  */
  /* the procedures and constraints described in Clause 23. */
  OBJECT_PROPRIETARY_MIN = 128,
  OBJECT_PROPRIETARY_MAX = 1023,
  /* do the max range inside of enum so that
     compilers will allocate adequate sized datatype for enum
     which is used to store decoding */
  MAX_BACNET_OBJECT_TYPE = 1024,
  /* special usage for this library 16-bit max */
  OBJECT_NONE = 65535
};


export enum  XAUTO_BACNET_ENGINEERING_UNITS{
  /* Enumerated values 0-255 are reserved for definition by ASHRAE. */
  /* Acceleration */
  UNITS_METERS_PER_SECOND_PER_SECOND = 166,
  /* Area */
  UNITS_SQUARE_METERS = 0,
  UNITS_SQUARE_CENTIMETERS = 116,
  UNITS_SQUARE_FEET = 1,
  UNITS_SQUARE_INCHES = 115,
  /* Currency */
  UNITS_CURRENCY1 = 105,
  UNITS_CURRENCY2 = 106,
  UNITS_CURRENCY3 = 107,
  UNITS_CURRENCY4 = 108,
  UNITS_CURRENCY5 = 109,
  UNITS_CURRENCY6 = 110,
  UNITS_CURRENCY7 = 111,
  UNITS_CURRENCY8 = 112,
  UNITS_CURRENCY9 = 113,
  UNITS_CURRENCY10 = 114,
  /* Efficiency */
  UNITS_BTU_PER_HOUR_PER_WATT = 47898,
  UNITS_BTU_PER_WATT_HOUR_SEASONAL = 47899,
  UNITS_COEFFICIENT_OF_PERFORMANCE = 47900,
  UNITS_COEFFICIENT_OF_PERFORMANCE_SEASONAL = 47901,
  UNITS_KILOWATT_PER_TON_REFRIGERATION = 47902,
  UNITS_LUMENS_PER_WATT = 47903,
  /* Electrical */
  UNITS_MILLIAMPERES = 2,
  UNITS_AMPERES = 3,
  UNITS_AMPERES_PER_METER = 167,
  UNITS_AMPERES_PER_SQUARE_METER = 168,
  UNITS_AMPERE_SQUARE_METERS = 169,
  UNITS_DECIBELS = 199,
  UNITS_DECIBELS_MILLIVOLT = 200,
  UNITS_DECIBELS_VOLT = 201,
  UNITS_FARADS = 170,
  UNITS_HENRYS = 171,
  UNITS_OHMS = 4,
  UNITS_OHM_METERS = 172,
  UNITS_MILLIOHMS = 145,
  UNITS_KILOHMS = 122,
  UNITS_MEGOHMS = 123,
  UNITS_MICROSIEMENS = 190,
  UNITS_MILLISIEMENS = 202,
  UNITS_SIEMENS = 173, /* 1 mho equals 1 siemens */
  UNITS_SIEMENS_PER_METER = 174,
  UNITS_MICROSIEMENS_PER_CENTIMETER = 47909,
  UNITS_MILLISIEMENS_PER_CENTIMETER = 47910,
  UNITS_MILLISIEMENS_PER_METER = 47911,
  UNITS_TESLAS = 175,
  UNITS_VOLTS = 5,
  UNITS_MILLIVOLTS = 124,
  UNITS_KILOVOLTS = 6,
  UNITS_MEGAVOLTS = 7,
  UNITS_VOLT_AMPERES = 8,
  UNITS_KILOVOLT_AMPERES = 9,
  UNITS_MEGAVOLT_AMPERES = 10,
  UNITS_VOLT_AMPERES_REACTIVE = 11,
  UNITS_KILOVOLT_AMPERES_REACTIVE = 12,
  UNITS_MEGAVOLT_AMPERES_REACTIVE = 13,
  UNITS_VOLTS_PER_DEGREE_KELVIN = 176,
  UNITS_VOLTS_PER_METER = 177,
  UNITS_DEGREES_PHASE = 14,
  UNITS_POWER_FACTOR = 15,
  UNITS_WEBERS = 178,
  /* Energy */
  UNITS_JOULES = 16,
  UNITS_KILOJOULES = 17,
  UNITS_KILOJOULES_PER_KILOGRAM = 125,
  UNITS_MEGAJOULES = 126,
  UNITS_GIGAJOULES = 47925,
  UNITS_TERAJOULES = 47926,
  UNITS_WATT_HOURS = 18,
  UNITS_KILOWATT_HOURS = 19,
  UNITS_MEGAWATT_HOURS = 146,
  UNITS_GIGAWATT_HOURS = 47927,
  UNITS_WATT_REACTIVE_HOURS = 203,
  UNITS_KILOWATT_REACTIVE_HOURS = 204,
  UNITS_MEGAWATT_REACTIVE_HOURS = 205,
  UNITS_GIGAWATT_REACTIVE_HOURS = 47928,
  UNITS_BTUS = 20,
  UNITS_KILO_BTUS = 147,
  UNITS_MEGA_BTUS = 148,
  UNITS_THERMS = 21,
  UNITS_TON_HOURS = 22,
  UNITS_ACTIVE_ENERGY_PULSE_VALUE = 47918,
  UNITS_REACTIVE_ENERGY_PULSE_VALUE = 47919,
  UNITS_APPARENT_ENERGY_PULSE_VALUE = 47920,
  UNITS_VOLT_SQUARED_HOUR_PULSE_VALUE = 47921,
  UNITS_AMPERE_SQUARED_HOUR_PULSE_VALUE = 47922,
  /* Enthalpy */
  UNITS_JOULES_PER_KILOGRAM_DRY_AIR = 23,
  UNITS_KILOJOULES_PER_KILOGRAM_DRY_AIR = 149,
  UNITS_MEGAJOULES_PER_KILOGRAM_DRY_AIR = 150,
  UNITS_BTUS_PER_POUND_DRY_AIR = 24,
  UNITS_BTUS_PER_POUND = 117,
  /* Entropy */
  UNITS_JOULES_PER_DEGREE_KELVIN = 127,
  UNITS_KILOJOULES_PER_DEGREE_KELVIN = 151,
  UNITS_MEGAJOULES_PER_DEGREE_KELVIN = 152,
  UNITS_JOULES_PER_KILOGRAM_DEGREE_KELVIN = 128,
  /* Force */
  UNITS_NEWTON = 153,
  /* Frequency */
  UNITS_CYCLES_PER_HOUR = 25,
  UNITS_CYCLES_PER_MINUTE = 26,
  UNITS_HERTZ = 27,
  UNITS_KILOHERTZ = 129,
  UNITS_MEGAHERTZ = 130,
  UNITS_PER_DAY = 47823,
  UNITS_PER_HOUR = 131,
  UNITS_PER_MILLISECOND = 47824,
  /* Humidity */
  UNITS_GRAMS_OF_WATER_PER_KILOGRAM_DRY_AIR = 28,
  UNITS_PERCENT_RELATIVE_HUMIDITY = 29,
  /* Length */
  UNITS_MICROMETERS = 194,
  UNITS_MILLIMETERS = 30,
  UNITS_CENTIMETERS = 118,
  UNITS_KILOMETERS = 193,
  UNITS_METERS = 31,
  UNITS_INCHES = 32,
  UNITS_FEET = 33,
  UNITS_YARDS = 47825,
  UNITS_MILES = 47826,
  UNITS_NAUTICAL_MILES = 47827,
  /* Light */
  UNITS_CANDELAS = 179,
  UNITS_CANDELAS_PER_SQUARE_METER = 180,
  UNITS_WATTS_PER_SQUARE_FOOT = 34,
  UNITS_WATTS_PER_SQUARE_METER = 35,
  UNITS_LUMENS = 36,
  UNITS_LUXES = 37,
  UNITS_FOOT_CANDLES = 38,
  /* Mass */
  UNITS_NANOGRAMS = 47828,
  UNITS_MICROGRAMS = 47829,
  UNITS_MILLIGRAMS = 196,
  UNITS_GRAMS = 195,
  UNITS_KILOGRAMS = 39,
  UNITS_POUNDS_MASS = 40,
  UNITS_TONS = 41,
  UNITS_METRIC_TONNES = 47830,
  UNITS_SHORT_TONS = 47831,
  UNITS_LONG_TONS = 47832,
  /* Mass Flow */
  UNITS_GRAMS_PER_SECOND = 154,
  UNITS_GRAMS_PER_MINUTE = 155,
  UNITS_GRAMS_PER_HOUR = 47833,
  UNITS_GRAMS_PER_DAY = 47834,
  UNITS_KILOGRAMS_PER_SECOND = 42,
  UNITS_KILOGRAMS_PER_MINUTE = 43,
  UNITS_KILOGRAMS_PER_HOUR = 44,
  UNITS_KILOGRAMS_PER_DAY = 47835,
  UNITS_POUNDS_MASS_PER_SECOND = 119,
  UNITS_POUNDS_MASS_PER_MINUTE = 45,
  UNITS_POUNDS_MASS_PER_HOUR = 46,
  UNITS_TONS_PER_HOUR = 156,
  UNITS_SHORT_TONS_PER_SECOND = 47836,
  UNITS_SHORT_TONS_PER_MINUTE = 47837,
  UNITS_SHORT_TONS_PER_HOUR = 47838,
  UNITS_SHORT_TONS_PER_DAY = 47839,
  UNITS_METRIC_TONNES_PER_SECOND = 47840,
  UNITS_METRIC_TONNES_PER_MINUTE = 47841,
  UNITS_METRIC_TONNES_PER_HOUR = 47842,
  UNITS_METRIC_TONNES_PER_DAY = 47843,
  UNITS_LONG_TONS_PER_SECOND = 47844,
  UNITS_LONG_TONS_PER_MINUTE = 47845,
  UNITS_LONG_TONS_PER_HOUR = 47846,
  UNITS_LONG_TONS_PER_DAY = 47847,
  /* Power */
  UNITS_MILLIWATTS = 132,
  UNITS_WATTS = 47,
  UNITS_KILOWATTS = 48,
  UNITS_MEGAWATTS = 49,
  UNITS_GIGAWATTS = 47924,
  UNITS_BTUS_PER_SECOND = 47848,
  UNITS_BTUS_PER_MINUTE = 47849,
  UNITS_BTUS_PER_HOUR = 50,
  UNITS_BTUS_PER_DAY = 47850,
  UNITS_KILO_BTUS_PER_SECOND = 47851,
  UNITS_KILO_BTUS_PER_MINUTE = 47852,
  UNITS_KILO_BTUS_PER_HOUR = 157,
  UNITS_KILO_BTUS_PER_DAY = 47853,
  UNITS_MEGA_BTUS_PER_SECOND = 47854,
  UNITS_MEGA_BTUS_PER_MINUTE = 47855,
  UNITS_MEGA_BTUS_PER_HOUR = 47856,
  UNITS_MEGA_BTUS_PER_DAY = 47857,
  UNITS_JOULES_PER_SECOND = 47858,
  UNITS_JOULES_PER_MINUTE = 47859,
  UNITS_JOULES_PER_DAY = 47860,
  UNITS_KILOJOULES_PER_SECOND = 47861,
  UNITS_KILOJOULES_PER_MINUTE = 47862,
  UNITS_KILOJOULES_PER_HOUR = 47863,
  UNITS_KILOJOULES_PER_DAY = 47864,
  UNITS_MEGAJOULES_PER_SECOND = 47865,
  UNITS_MEGAJOULES_PER_MINUTE = 47866,
  UNITS_MEGAJOULES_PER_HOUR = 47867,
  UNITS_MEGAJOULES_PER_DAY = 47868,
  UNITS_HORSEPOWER = 51,
  UNITS_TONS_REFRIGERATION = 52,
  /* Pressure */
  UNITS_PASCALS = 53,
  UNITS_HECTOPASCALS = 133,
  UNITS_KILOPASCALS = 54,
  UNITS_MILLIBARS = 134,
  UNITS_BARS = 55,
  UNITS_POUNDS_FORCE_PER_SQUARE_INCH = 56,
  UNITS_POUNDS_FORCE_PER_SQUARE_INCH_ABSOLUTE = 47907,
  UNITS_POUNDS_FORCE_PER_SQUARE_INCH_GAUGE = 47908,
  UNITS_MILLIMETERS_OF_WATER = 206,
  UNITS_CENTIMETERS_OF_WATER = 57,
  UNITS_INCHES_OF_WATER = 58,
  UNITS_MILLIMETERS_OF_MERCURY = 59,
  UNITS_CENTIMETERS_OF_MERCURY = 60,
  UNITS_INCHES_OF_MERCURY = 61,
  /* Temperature */
  UNITS_DEGREES_CELSIUS = 62,
  UNITS_DEGREES_CELSIUS_PER_DAY = 47869,
  UNITS_KELVIN = 63,
  UNITS_KELVIN_PER_DAY = 47870,
  UNITS_KELVIN_PER_HOUR = 181,
  UNITS_KELVIN_PER_MINUTE = 182,
  UNITS_DEGREES_FAHRENHEIT = 64,
  UNITS_DEGREES_FAHRENHEIT_PER_DAY = 47871,
  UNITS_DEGREE_DAYS_CELSIUS = 65,
  UNITS_DEGREE_DAYS_FAHRENHEIT = 66,
  UNITS_DELTA_DEGREES_CELSIUS = 47872,
  UNITS_DELTA_DEGREES_FAHRENHEIT = 120,
  UNITS_DELTA_KELVIN = 121,
  /* Time */
  UNITS_YEARS = 67,
  UNITS_MONTHS = 68,
  UNITS_WEEKS = 69,
  UNITS_DAYS = 70,
  UNITS_HOURS = 71,
  UNITS_MINUTES = 72,
  UNITS_SECONDS = 73,
  UNITS_HUNDREDTHS_SECONDS = 158,
  UNITS_MILLISECONDS = 159,
  /* Torque */
  UNITS_NEWTON_METERS = 160,
  UNITS_POUND_FORCE_FEET = 47904,
  UNITS_POUND_FORCE_INCHES = 47905,
  UNITS_OUNCE_FORCE_INCHES = 47906,
  /* Velocity */
  UNITS_MILLIMETERS_PER_SECOND = 161,
  UNITS_MILLIMETERS_PER_MINUTE = 162,
  UNITS_METERS_PER_SECOND = 74,
  UNITS_METERS_PER_MINUTE = 163,
  UNITS_METERS_PER_HOUR = 164,
  UNITS_KILOMETERS_PER_HOUR = 75,
  UNITS_FEET_PER_SECOND = 76,
  UNITS_FEET_PER_MINUTE = 77,
  UNITS_MILES_PER_HOUR = 78,
  /* Volume */
  UNITS_CUBIC_FEET = 79,
  UNITS_CUBIC_METERS = 80,
  UNITS_IMPERIAL_GALLONS = 81,
  UNITS_MILLILITERS = 197,
  UNITS_LITERS = 82,
  UNITS_US_GALLONS = 83,
  UNITS_MILLIONS_OF_US_GALLONS = 47912,
  UNITS_MILLIONS_OF_IMPERIAL_GALLONS = 47913,
  UNITS_VOLUME1 = 47937,
  UNITS_VOLUME2 = 47938,
  UNITS_VOLUME3 = 47939,
  UNITS_VOLUME4 = 47940,
  UNITS_VOLUME5 = 47941,
  UNITS_VOLUME6 = 47942,
  UNITS_VOLUME7 = 47943,
  UNITS_VOLUME8 = 47944,
  UNITS_VOLUME9 = 47945,
  UNITS_VOLUME10 = 47946,
  /* Volumetric Flow */
  UNITS_CUBIC_FEET_PER_SECOND = 142,
  UNITS_CUBIC_FEET_PER_MINUTE = 84,
  UNITS_CUBIC_FEET_PER_HOUR = 191,
  UNITS_MILLION_CUBIC_FEET_PER_MINUTE = 47873,
  UNITS_MILLION_CUBIC_FEET_PER_DAY = 47874,
  UNITS_CUBIC_METERS_PER_SECOND = 85,
  UNITS_CUBIC_METERS_PER_MINUTE = 165,
  UNITS_CUBIC_METERS_PER_HOUR = 135,
  UNITS_IMPERIAL_GALLONS_PER_SECOND = 47875,
  UNITS_IMPERIAL_GALLONS_PER_MINUTE = 86,
  UNITS_IMPERIAL_GALLONS_PER_HOUR = 47876,
  UNITS_IMPERIAL_GALLONS_PER_DAY = 47877,
  UNITS_MILLILITERS_PER_SECOND = 198,
  UNITS_MILLILITERS_PER_MINUTE = 47914,
  UNITS_LITERS_PER_SECOND = 87,
  UNITS_LITERS_PER_MINUTE = 88,
  UNITS_LITERS_PER_HOUR = 136,
  UNITS_LITERS_PER_DAY = 47878,
  UNITS_US_GALLONS_PER_SECOND = 47879,
  UNITS_US_GALLONS_PER_MINUTE = 89,
  UNITS_US_GALLONS_PER_HOUR = 192,
  UNITS_US_GALLONS_PER_DAY = 47880,
  UNITS_CUBIC_METER_PULSE_VALUE = 47923,
  UNITS_VOLUMETRIC_FLOW1 = 47947,
  UNITS_VOLUMETRIC_FLOW2 = 47948,
  UNITS_VOLUMETRIC_FLOW3 = 47949,
  UNITS_VOLUMETRIC_FLOW4 = 47950,
  UNITS_VOLUMETRIC_FLOW5 = 47951,
  UNITS_VOLUMETRIC_FLOW6 = 47952,
  UNITS_VOLUMETRIC_FLOW7 = 47953,
  UNITS_VOLUMETRIC_FLOW8 = 47954,
  UNITS_VOLUMETRIC_FLOW9 = 47955,
  UNITS_VOLUMETRIC_FLOW10 = 47956,
  /* Other */
  UNITS_DEGREES_ANGULAR = 90,
  UNITS_DEGREES_CELSIUS_PER_HOUR = 91,
  UNITS_DEGREES_CELSIUS_PER_MINUTE = 92,
  UNITS_DEGREES_FAHRENHEIT_PER_HOUR = 93,
  UNITS_DEGREES_FAHRENHEIT_PER_MINUTE = 94,
  UNITS_JOULE_SECONDS = 183,
  UNITS_KILOGRAMS_PER_CUBIC_METER = 186,
  UNITS_KW_HOURS_PER_SQUARE_METER = 137,
  UNITS_KW_HOURS_PER_SQUARE_FOOT = 138,
  UNITS_MEGAJOULES_PER_SQUARE_METER = 139,
  UNITS_MEGAJOULES_PER_SQUARE_FOOT = 140,
  UNITS_NO_UNITS = 95,
  UNITS_NEWTON_SECONDS = 187,
  UNITS_NEWTONS_PER_METER = 188,
  UNITS_PARTS_PER_MILLION = 96,
  UNITS_PARTS_PER_BILLION = 97,
  UNITS_PERCENT = 98,
  UNITS_PERCENT_OBSCURATION_PER_FOOT = 143,
  UNITS_PERCENT_OBSCURATION_PER_METER = 144,
  UNITS_PERCENT_PER_SECOND = 99,
  UNITS_PERCENT_PER_MINUTE = 47881,
  UNITS_PERCENT_PER_HOUR = 47882,
  UNITS_PERCENT_PER_DAY = 47883,
  UNITS_PER_MINUTE = 100,
  UNITS_PER_SECOND = 101,
  UNITS_PSI_PER_DEGREE_FAHRENHEIT = 102,
  UNITS_RADIANS = 103,
  UNITS_RADIANS_PER_SECOND = 184,
  UNITS_REVOLUTIONS_PER_MINUTE = 104,
  UNITS_SQUARE_METERS_PER_NEWTON = 185,
  UNITS_WATTS_PER_METER_PER_DEGREE_KELVIN = 189,
  UNITS_WATTS_PER_SQUARE_METER_DEGREE_KELVIN = 141,
  UNITS_PER_MILLE = 207,
  UNITS_PER_MILLION = 47884,
  UNITS_PER_BILLION = 47885,
  UNITS_GRAMS_PER_GRAM = 208,
  UNITS_MICROGRAMS_PER_GRAM = 47886,
  UNITS_NANOGRAMS_PER_GRAM = 47887,
  UNITS_KILOGRAMS_PER_KILOGRAM = 209,
  UNITS_GRAMS_PER_KILOGRAM = 210,
  UNITS_MILLIGRAMS_PER_GRAM = 211,
  UNITS_MILLIGRAMS_PER_KILOGRAM = 212,
  UNITS_MICROGRAMS_PER_KILOGRAM = 47888,
  UNITS_NANOGRAMS_PER_KILOGRAM = 47889,
  UNITS_GRAMS_PER_MILLILITER = 213,
  UNITS_MILLIGRAMS_PER_MILLILITER = 47890,
  UNITS_MICROGRAMS_PER_MILLILITER = 47891,
  UNITS_NANOGRAMS_PER_MILLILITER = 47892,
  UNITS_KILOGRAMS_PER_LITER = 47893,
  UNITS_GRAMS_PER_LITER = 214,
  UNITS_MILLIGRAMS_PER_LITER = 215,
  UNITS_MICROGRAMS_PER_LITER = 216,
  UNITS_NANOGRAMS_PER_LITER = 47894,
  UNITS_GRAMS_PER_CUBIC_METER = 217,
  UNITS_MILLIGRAMS_PER_CUBIC_METER = 218,
  UNITS_MICROGRAMS_PER_CUBIC_METER = 219,
  UNITS_NANOGRAMS_PER_CUBIC_METER = 220,
  UNITS_GRAMS_PER_CUBIC_CENTIMETER = 221,
  UNITS_MILLIGRAMS_PER_CUBIC_CENTIMETER = 47895,
  UNITS_MICROGRAMS_PER_CUBIC_CENTIMETER = 47896,
  UNITS_NANOGRAMS_PER_CUBIC_CENTIMETER = 47897,
  UNITS_BECQUERELS = 222,
  UNITS_KILOBECQUERELS = 223,
  UNITS_MEGABECQUERELS = 224,
  UNITS_GRAY = 225,
  UNITS_MILLIGRAY = 226,
  UNITS_MICROGRAY = 227,
  UNITS_SIEVERTS = 228,
  UNITS_MILLISIEVERTS = 229,
  UNITS_MICROSIEVERTS = 230,
  UNITS_MICROSIEVERTS_PER_HOUR = 231,
  UNITS_DECIBELS_A = 232,
  UNITS_NEPHELOMETRIC_TURBIDITY_UNIT = 233,
  UNITS_PH = 234,
  UNITS_GRAMS_PER_SQUARE_METER = 235,
  UNITS_MINUTES_PER_DEGREE_KELVIN = 236,
  UNITS_DEGREES_LOVIBOND = 47816,
  UNITS_ALCOHOL_BY_VOLUME = 47817,
  UNITS_INTERNATIONAL_BITTERING_UNITS = 47818,
  UNITS_EUROPEAN_BITTERNESS_UNITS = 47819,
  UNITS_DEGREES_PLATO = 47820,
  UNITS_SPECIFIC_GRAVITY = 47821,
  UNITS_EUROPEAN_BREWING_CONVENTION = 47822,
  UNITS_MILS_PER_YEAR = 47915,
  UNITS_MILLIMETERS_PER_YEAR = 47916,
  UNITS_PULSES_PER_MINUTE = 47917,
  UNITS_BITS_PER_SECOND = 47929,
  UNITS_KILOBITS_PER_SECOND = 47930,
  UNITS_MEGABITS_PER_SECOND = 47931,
  UNITS_GIGABITS_PER_SECOND = 47932,
  UNITS_BYTES_PER_SECOND = 47933,
  UNITS_KILOBYTES_PER_SECOND = 47934,
  UNITS_MEGABYTES_PER_SECOND = 47935,
  UNITS_GIGABYTES_PER_SECOND = 47936,
  UNITS_SITE_UNIT1 = 47958,
  UNITS_SITE_UNIT2 = 47959,
  UNITS_SITE_UNIT3 = 47960,
  UNITS_SITE_UNIT4 = 47961,
  UNITS_SITE_UNIT5 = 47962,
  UNITS_SITE_UNIT6 = 47963,
  UNITS_SITE_UNIT7 = 47964,
  UNITS_SITE_UNIT8 = 47965,
  UNITS_SITE_UNIT9 = 47966,
  UNITS_SITE_UNIT10 = 47967,

  UNITS_OHM_METER_SQUARED_PER_METER = 237,
  UNITS_AMPERE_SECONDS = 238,
  UNITS_VOLT_AMPERE_HOURS = 239,
  UNITS_KILOVOLT_AMPERE_HOURS = 240,
  UNITS_MEGAVOLT_AMPERE_HOURS = 241,
  UNITS_VOLT_AMPERE_REACTIVE_HOURS = 242,
  UNITS_KILOVOLT_AMPERE_REACTIVE_HOURS = 243,
  UNITS_MEGAVOLT_AMPERE_REACTIVE_HOURS = 244,
  UNITS_VOLT_SQUARE_HOURS = 245,
  UNITS_AMPERE_SQUARE_HOURS = 246,
  UNITS_JOULE_PER_HOURS = 247,
  UNITS_CUBIC_FEET_PER_DAY = 248,
  UNITS_CUBIC_METERS_PER_DAY = 249,
  UNITS_WATT_HOURS_PER_CUBIC_METER = 250,
  UNITS_JOULES_PER_CUBIC_METER = 251,
  UNITS_MOLE_PERCENT = 252,
  UNITS_PASCAL_SECONDS = 253,
  UNITS_MILLION_STANDARD_CUBIC_FEET_PER_MINUTE = 254,
  UNITS_RESERVED_RANGE_MAX = 255,
  /* Enumerated values 256-47807 may be used by others
     subject to the procedures and constraints described in Clause 23. */
  UNITS_PROPRIETARY_RANGE_MIN = 256,
  UNITS_PROPRIETARY_RANGE_MAX = 47807,
  /* Enumerated values 47808-49999 are reserved for definition by ASHRAE. */
  UNITS_RESERVED_RANGE_MIN2 = 47808,
  UNITS_STANDARD_CUBIC_FEET_PER_DAY = 47808,
  UNITS_MILLION_STANDARD_CUBIC_FEET_PER_DAY = 47809,
  UNITS_THOUSAND_CUBIC_FEET_PER_DAY = 47810,
  UNITS_THOUSAND_STANDARD_CUBIC_FEET_PER_DAY = 47811,
  UNITS_POUNDS_MASS_PER_DAY = 47812,
  /* 47813 - NOT USED */
  UNITS_MILLIREMS = 47814,
  UNITS_MILLIREMS_PER_HOUR = 47815,
  UNITS_RESERVED_RANGE_MAX2 = 49999,
  UNITS_PROPRIETARY_RANGE_MIN2 = 50000,
  /* Enumerated values 50000-65535 may be used by others
     subject to the procedures and constraints described in Clause 23. */
  /* do the proprietary range inside of enum so that
     compilers will allocate adequate sized datatype for enum
     which is used to store decoding */
  UNITS_PROPRIETARY_RANGE_MAX2 = 65535
};

export class XAUTO_XAutoVariableBacnet
{
  public xautodriverid : string;
  public xautovariableid : string;

  public deviceinstance : number;
  public type : XAUTO_BACNET_OBJECT_TYPE;
  public units : XAUTO_BACNET_ENGINEERING_UNITS;
  public index : number;
  public autoimported : boolean = false;
}

export class XAUTO_XAutoVariableWithBacnet
{
  public variable : XAUTO_XAutoVariable;
  public bacnet: XAUTO_XAutoVariableBacnet;
  public lastupdated : Date;
}

export enum XAUTO_IntegrationServerType
{
  XAUTO	= 0,
  MQTT 	= 10,
  BACNET	= 20,
  MODBUS	= 30,
  OPCUA	= 40
};

export enum XAUTO_IntegrationServerDirection
{
  IN 		= 0,
  OUT 	= 1,
  BI 		= 2
};

export class XAUTO_XAutoGroupLinkIntegrationServer
{
  public xautogroup: string = "";
  public xgroup: string = "";
};

export class XAUTO_XAutoIntegrationServer
{
  public xautointegrationserverid: string = "";
  public name:string = "";
  public description: string = "";
  public enable: boolean = false;
  public servertype: XAUTO_IntegrationServerType = XAUTO_IntegrationServerType.MQTT;
  public direction: XAUTO_IntegrationServerDirection = XAUTO_IntegrationServerDirection.OUT;
  public xautogroupfilter: Array <XAUTO_XAutoGroupLinkIntegrationServer>=new Array<XAUTO_XAutoGroupLinkIntegrationServer>();
  public requireexplicitmapping : boolean = false;
};

export class XAUTO_IntegrationServerMqtt
{
  public xautointegrationserverid : string = "";
  public uri : string = "";
  public clientid:string = "";
  public username:string = "";
  public password:string = "";
  public validateservercertificate: boolean = true;
  public luascriptdatain : string = "";
  public luascriptdataout : string = "";
};

export class XAUTO_IntegrationServerMqttVariableMapping
{
  public integrationservermqttvariablemappingid :string = "";
  public xautointegrationserverid : string = "";
  public xautogroupfilter : Array<XAUTO_XAutoGroupLinkIntegrationServer> = new Array<XAUTO_XAutoGroupLinkIntegrationServer>();
  public xautovariableid : string = "";
  public externalid = "";
  public topic = "";
  public key = "";
  public luascriptout = "";
  public luascriptin = "";
}

// Highlevel client to be used by consumers
@Injectable({
  providedIn: 'root'
})
export class XProjectorClient implements OnDestroy
{
  public authenticatedSubject: Subject<void> = new Subject();
  public disconnectedSubject: Subject<void> = new Subject();
  public messageSubject: Subject<Object> = new Subject();

  public projectionDataChangedSubject: Subject<string> = new Subject();
  public projectionDataResetedSubject: Subject<string> = new Subject();
  public projectionChangedSubject: Subject<string> = new Subject();
  public datasourceChangedSubject: Subject<string> = new Subject();

  private client : XProjectorLowlevelClient;

  private ngUnsubscribe = new Subject<void>();
  private keepAliveTimerSource;
  private keepAliveActive : boolean = false;

  public constructor(private readonly cache: CacheService,
        @Inject(LOGGERSERVICE) private logger: XprojLoggerService,
        @Inject(XPROJECTORCLIENTSETTINGS) public settings : XProjectClientSettings)
  {
    if (settings.cacheEnabled == undefined) {
      settings.cacheEnabled = cacheEnabled;
    }
    if (settings.cacheTTLSeconds == undefined) {
      settings.cacheTTLSeconds = cacheTTLSeconds;
    }
    if (settings.keepAlive == undefined) {
      settings.keepAlive = true;
    }
    if (settings.keepAliveInterval == undefined) {
      settings.keepAliveInterval = 5000;
    }

    this.client  = new XProjectorLowlevelClient(settings, logger);
    this.client.OnMessage = this.HandleOnMessage.bind(this);
    this.client.OnDisconnected = this.HandleOnDisconnect.bind(this);
    this.client.OnAuthenticated = this.HandleOnAuthenticated.bind(this);
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  public SetHostAndToken(host : string, token : string) {
    this.client?.SetHost(host);
    if (this.client?.isConnected) {
      this.client.Disconnect();
    }
    this.Connect(token);
  }

  async HandleOnAuthenticated()
  {
    this.authenticatedSubject.next();
    this.stopKeepAlive();
  }

  async HandleOnDisconnect()
  {

    for(let [key, value] of this.ActiveResponsesResolve)
    {
      let promiseresolver =  this.ActiveResponsesResolve.get(key);
      promiseresolver.reject('Disconnected');
    }

    this.ActiveResponsesResolve.clear();

    this.disconnectedSubject.next();

    this.startKeepAlive();
  }

  async Connect(token : string = '')
  {
    this.ActiveResponsesResolve.clear();

    if ((!token || token.length == 0) && this.settings.defaultToken?.length > 0) {
      this.client.Connect(this.settings.defaultToken);
    }
    else {
      this.client.Connect(token);
    }

    this.ActiveResponsesResolve.clear();
  }

  SetActiveRemoteNodeID(remotenodeid: string)
  {
    this.client.SetActiveRemoteNodeID(remotenodeid);
  }

  IsConnected() : boolean {
    return this.client.isConnected;
  }

  async Disconnect()
  {
    this.client.Disconnect();
  }

  startKeepAlive() {
    if (this.settings.keepAlive && !this.keepAliveActive) {
      this.keepAliveActive = true;
      this.keepAliveTimerSource = timer(this.settings.keepAliveInterval, this.settings.keepAliveInterval);

      this.keepAliveTimerSource.pipe(takeUntil(this.ngUnsubscribe)).subscribe(async (event) => {
        if (!this.client.isConnected || !this.client.isAuthenticated) {
          this.Connect();
        }
      });
    }
  }

  stopKeepAlive() {
    this.ngUnsubscribe.next();
    this.keepAliveActive = false;
  }


  private ActiveResponsesResolve : Map<number, PromiseResolve> = new Map<number, PromiseResolve>();

  async HandleEvent(EventType: string, Event: Object)
  {
    if(EventType == "projectiondatachanged")
    {
      let projectionId = Event["projectionid"];
      this.projectionDataChangedSubject.next(projectionId);
      this.logger.debug("I got a projection data changed on projection " + projectionId);
    }
    if(EventType == "projectiondataresetted")
    {
      let projectionId = Event["projectionid"];
      this.projectionDataResetedSubject.next(projectionId);
      this.logger.debug("I got a projection data resetted on projection " + projectionId);
    }
    if(EventType == "projectionchanged")
    {
      let projectionId = Event["projectionid"];
      this.projectionChangedSubject.next(projectionId);
      this.logger.debug("I got a projection config changed " + projectionId);
    }
    if(EventType == "datasourcechanged")
    {
      let datasourceId = Event["datasourceid"];
      this.datasourceChangedSubject.next(datasourceId);
      this.logger.debug("I got a datasource changed on datasource " + datasourceId);
    }

  }

  async HandleOnMessage(Data:Object)
  {
    // Todo: Check if its event type first
    let EventType = Data["eventtype"];
    if(EventType && EventType != "")
    {
      await this.HandleEvent(EventType, Data);

      // Beware this may break stuff?
      this.messageSubject.next(Data);

      return;
    }

    let MessageBody = Data["XProjector::MessagePack::Response"];
    let ContentBody = Data["Data"];

    if(!MessageBody)
      MessageBody = Data;

	  let msgid=MessageBody["messageid"];

    if(this.ActiveResponsesResolve.has(msgid))
    {
      let promiseresolver = this.ActiveResponsesResolve.get(msgid);
      if(promiseresolver)
      {
		    let succes : boolean = MessageBody["success"];
		  //this.logger.info('success', succes);

		  if (succes) {
			  promiseresolver.resolve( ContentBody );
		  }
		  else {
        //this.logger.info(Data);
			  let errormessage = MessageBody["errormessage"];
			  promiseresolver.reject(errormessage?.length > 0 ? errormessage :  'unknown error');
		  }
	  }
	  this.ActiveResponsesResolve.delete(msgid);
      return;
    }

    this.messageSubject.next(Data);
  }


  async RequestDevDatasourceProbeqQuery( query: DataSourceProbeQuery ) : Promise<DataSourceProbeQueryResult>
  {
    let msg= {
      request: "dev-datasource-probequery",
      data: query
    };

    let rval = await this.Request(msg);
    return rval[0] as BaseQueryResult;
  }

  async RequestQueryGroups(query:GroupsQuery) : Promise<GroupsQueryResult>
  {
    if (!query.targetprojectionid || query.targetprojectionid.length == 0) {
      return Promise.reject('No projectionid provided.');
    }

    let msg= {
      request: "query-groups",
      data: query
    };

    let rval = (await this.Request(msg))[0] as GroupsQueryResult;
    //this.logger.info(rval)
    return rval;
  }

  async RequestQueryBaseQuery( query: BaseQuery, forcereload : boolean = false, sender : string = '', senderId : string = '') : Promise<BaseQueryResult>
  {
    if (!query.targetprojectionid || query.targetprojectionid.length == 0) {
      return Promise.reject('No projectionid provided.');
    }

    let msg= {
      request: "query-basequery",
      data: query
    };
    this.logger.debug('RequestQueryBaseQuery', 'sender: ' + sender, 'senderid: ' + senderId, query);

    let rval = (await this.RequestWithCache(msg, forcereload))[0] as BaseQueryResult;
    //this.logger.info(rval)
    this.logger.debug('RequestQueryBaseQuery response', 'sender: ' + sender, 'senderid: ' + senderId, rval);
    return rval;
  }

  async RequestQueryDownSampleQuery( query: DownSampleQuery, forcereload : boolean = false, sender : string = '', senderId : string = '' ) : Promise<BaseQueryResult>
  {
    if (!query.query?.targetprojectionid || query.query.targetprojectionid.length == 0) {
      return Promise.reject('No projectionid provided.');
    }

    let msg= {
      request: "query-downsamplequery",
      data: query
    };
    this.logger.debug('RequestQueryDownSampleQuery', 'sender: ' + sender, 'senderid: ' + senderId, query);
    let rval = (await this.RequestWithCache(msg, forcereload))[0] as BaseQueryResult;
    this.logger.debug('RequestQueryDownSampleQuery response', 'sender: ' + sender, 'senderid: ' + senderId, rval);
    return rval;
  }

  async RequestQueryDFTQuery( query: DFTQuery, forcereload : boolean = false, sender : string = '', senderId : string = '' ) : Promise<BaseQueryResult>
  {
    if (!query.query.targetprojectionid || query.query.targetprojectionid.length == 0) {
      return Promise.reject('No projectionid provided.');
    }

    let msg= {
      request: "query-dftquery",
      data: query
    };

    this.logger.debug('RequestQueryDFTQuery', 'sender: ' + sender, 'senderid: ' + senderId, query);
    let rval =  (await this.RequestWithCache(msg, forcereload))[0] as BaseQueryResult;
    this.logger.debug('RequestQueryDFTQuery response', 'sender: ' + sender, 'senderid: ' + senderId, rval);
    //this.logger.info(rval);
    return rval;
  }


  async RequestQuerySPCQuery( query: SPCQuery, forcereload : boolean = false, sender : string = '', senderId : string = '' ) : Promise<BaseQueryResult>
  {
    if (!query.query.targetprojectionid || query.query.targetprojectionid.length == 0) {
      return Promise.reject('No projectionid provided.');
    }

    let msg= {
      request: "query-spcquery",
      data: query
    };

    this.logger.debug('RequestQuerySPCQuery', 'sender: ' + sender, 'senderid: ' + senderId, query);
    let rval =  (await this.RequestWithCache(msg, forcereload))[0] as BaseQueryResult;
    this.logger.debug('RequestQuerySPCQuery response', 'sender: ' + sender, 'senderid: ' + senderId, rval);
    //this.logger.info(rval);
    return rval;
  }

  async RequestQueryMultiseriesQuery( query: MultiseriesQuery, forcereload : boolean = false, sender : string = '', senderId : string = '' ) : Promise<BaseQueryResult>
  {
    query.queries.forEach(query => {
      if (!query.targetprojectionid || query.targetprojectionid.length == 0) {
        return Promise.reject('No projectionid provided.');
      }
    });

    let msg= {
      request: "query-multiseries",
      data: query
    };
    this.logger.debug('RequestQueryMultiseriesQuery', 'sender: ' + sender, 'senderid: ' + senderId, query);
    let rval = (await this.RequestWithCache(msg, forcereload))[0] as BaseQueryResult;
    this.logger.debug('RequestQueryMultiseriesQuery response', 'sender: ' + sender, 'senderid: ' + senderId, rval);

    return rval;
  }

  async RequestQueryTextsearch( query: TextSearchQuery ) : Promise<TextSearchQueryResult>
  {
    if (!query.targetprojectionid || query.targetprojectionid.length == 0) {
      return Promise.reject('No projectionid provided.');
    }

    let msg= {
      request: "query-textsearch",
      data: query
    };

    return (await this.Request(msg))[0] as TextSearchQueryResult;
  }



  async RequestSaveProjection( projection: Projection, overridelastprojected:boolean ) : Promise<Projection>
  {
    let msg= {
      request: "save-config-projection",
      overridelastprojectedatutc : overridelastprojected,
      data: projection
    };

    return (await this.Request(msg))[0] as Projection;
  }

  async RequestSaveProjectionColumnDescription( column: ProjectionColumnDescription ) : Promise<ProjectionColumnDescription>
  {
    let msg= {
      request: "save-config-projection-column",
      data: column
    };

    return (await this.Request(msg))[0] as ProjectionColumnDescription;
  }

  async RequestListConfigSchedulingQueues() : Promise<Array<String>>
  {
    let msg= {
      request: "list-config-schedulingqueues",
      path: "/",
    };

    let result = (await this.Request(msg)) as Array<String>;

    return result;
  }

  async RequestListConfigDataPaths() : Promise<Array<String>>
  {
    let msg= {
      request: "list-config-datapaths",
      path: "/",
    };

    let result = (await this.Request(msg)) as Array<String>;

    return result;
  }

  async RequestListQueryableProjections(from: number, maxitems: number) : Promise<Array<Projection>>
  {
    let msg= {
      request: "list-queryable-projections",
      maxitems: maxitems,
      seekoffset: from,
      path: "/",
    };

    let result = (await this.Request(msg)) as Array<Projection>;

    return result.sort((a, b) => a.name.toLocaleLowerCase() > b.name.toLocaleLowerCase() ? 1 : -1);
  }

  async RequestSaveDatasource( datasource: DataSource ) : Promise<DataSource>
  {
    let msg= {
      request: "save-config-datasource",
      data: datasource
    };

    return (await this.Request(msg))[0] as DataSource;
  }
  async RequestSaveDatasourceSQLServer( datasource: DataSourceSQLServer ) : Promise<DataSourceSQLServer>
  {
    let msg= {
      request: "save-config-datasource-sqlserver",
      data: datasource
    };

    return (await this.Request(msg))[0] as DataSourceSQLServer;
  }
  async RequestSaveDatasourcePostgreSQL( datasource: DataSourcePostgreSQL ) : Promise<DataSourcePostgreSQL>
  {
    let msg= {
      request: "save-config-datasource-postgresql",
      data: datasource
    };

    return (await this.Request(msg))[0] as DataSourcePostgreSQL;
  }
  async RequestSaveDatasourceMySQL( datasource: DataSourceMySQL ) : Promise<DataSourceMySQL>
  {
    let msg= {
      request: "save-config-datasource-mysql",
      data: datasource
    };

    return (await this.Request(msg))[0] as DataSourceMySQL;
  }
  async RequestSaveDatasourceSQLite( datasource: DataSourceSQLite ) : Promise<DataSourceSQLite>
  {
    let msg= {
      request: "save-config-datasource-sqlite",
      data: datasource
    };

    return (await this.Request(msg))[0] as DataSourceSQLite;
  }
  async RequestSaveDatasourceMongoDB( datasource: DataSourceMongoDB ) : Promise<DataSourceMongoDB>
  {
    let msg= {
      request: "save-config-datasource-mongodb",
      data: datasource
    };

    return (await this.Request(msg))[0] as DataSourceMongoDB;
  }

  async RequestSaveProjectionExecution( exec: ProjectionExecution ) : Promise<ProjectionExecution>
  {
    let msg= {
      request: "save-config-projection-execution",
      data: exec
    };

    return (await this.Request(msg))[0] as ProjectionExecution;
  }

  async RequestSaveProjectionExecutionDataSourceQuery( execds: ProjectionExecutionDataSourceQuery ) : Promise<ProjectionExecutionDataSourceQuery>
  {
    let msg= {
      request: "save-config-projection-execution-datasourcequery",
      data: execds
    };

    return (await this.Request(msg))[0] as ProjectionExecutionDataSourceQuery;
  }

  async RequestSaveProjectionExecutionLuaMainfunction( execlmf: ProjectionExecutionLuaMainFunction ) : Promise<ProjectionExecutionLuaMainFunction>
  {
    let msg= {
      request: "save-config-projection-execution-luamainfunction",
      data: execlmf
    };

    return (await this.Request(msg))[0] as ProjectionExecutionLuaMainFunction;
  }

  async RequestGetConfigDatasource(Datasourceid : string) : Promise<DataSource>
  {
    let msg= {
      request: "get-config-datasource",
      datasourceid: Datasourceid,
      path: "/",
    };

    return (await this.Request(msg))[0] as DataSource;
  }

  async RequestGetConfigDatasourceSQLServer(Datasourceid : string) : Promise<DataSourceSQLServer>
  {
    let msg= {
      request: "get-config-datasource-sqlserver",
      datasourceid: Datasourceid,
      path: "/",
    };

    return (await this.Request(msg))[0] as DataSourceSQLServer;
  }

  async RequestGetConfigDatasourcePostgreSQL(Datasourceid : string) : Promise<DataSourcePostgreSQL>
  {
    let msg= {
      request: "get-config-datasource-postgresql",
      datasourceid: Datasourceid,
      path: "/",
    };

    return (await this.Request(msg))[0] as DataSourcePostgreSQL;
  }

  async RequestGetConfigDatasourceMySQL(Datasourceid : string) : Promise<DataSourceMySQL>
  {
    let msg= {
      request: "get-config-datasource-mysql",
      datasourceid: Datasourceid,
      path: "/",
    };

    return (await this.Request(msg))[0] as DataSourceMySQL;
  }

  async RequestGetConfigDatasourceSQLite(Datasourceid : string) : Promise<DataSourceSQLite>
  {
    let msg= {
      request: "get-config-datasource-sqlite",
      datasourceid: Datasourceid,
      path: "/",
    };

    return (await this.Request(msg))[0] as DataSourceSQLite;
  }

  async RequestGetConfigDatasourceMongoDB(Datasourceid : string) : Promise<DataSourceMongoDB>
  {
    let msg= {
      request: "get-config-datasource-mongodb",
      datasourceid: Datasourceid,
      path: "/",
    };

    return (await this.Request(msg))[0] as DataSourceMongoDB;
  }

  async RequestListConfigDatasources(from: number, maxitems: number) : Promise<Array<DataSource>>
  {
    let msg= {
      request: "list-config-datasources",
      maxitems: maxitems,
      seekoffset: from,
      path: "/",
    };

    let result = (await this.Request(msg)) as Array<DataSource>;
    return result.sort((a, b) => a.name.toLocaleLowerCase() > b.name.toLocaleLowerCase() ? 1 : -1);
  }

  async RequestListConfigProjections(from: number, maxitems: number) : Promise<Array<Projection>>
  {
    let msg= {
      request: "list-config-projections",
      maxitems: maxitems,
      seekoffset: from,
      path: "/"
    };

    let result = (await this.Request(msg)) as Array<Projection>;
    return result.sort((a, b) => a.name.toLocaleLowerCase() > b.name.toLocaleLowerCase() ? 1 : -1);
  }

  async RequestListQueryableProjectionGroups(projectionId:string, from: number, maxitems: number) : Promise<Array<Array<string>>>
  {
    let msg= {
      request: "list-queryable-projection-groups",
      projectionid: projectionId,
      maxitems: maxitems,
      seekoffset: from,
      path: "/",
    };

    return (await this.Request(msg)) as Array<Array<string>>;
  }

  async RequestListQueryableProjectionColumns(projectionId:string, group: string, from: number, maxitems: number) : Promise<Array<ProjectionColumnDescription>>
  {
    let msg= {
      request: "list-queryable-projection-columns",
      projectionid: projectionId,
      maxitems: maxitems,
      seekoffset: from,
      group: group,
      path: "/",
    };

    let result = (await this.Request(msg)) as Array<ProjectionColumnDescription>;
    return result.sort((a, b) => a.columnname.toLocaleLowerCase() > b.columnname.toLocaleLowerCase() ? 1 : -1);
  }

  async RequestListConfigProjectionExecutions(projectionId:string, from: number, maxitems: number) : Promise<Array<ProjectionExecution>>
  {
    let msg= {
      request: "list-config-projection-executions",
      projectionid: projectionId,
      maxitems: maxitems,
      seekoffset: from,
      path: "/",
    };

    return (await this.Request(msg)) as Array<ProjectionExecution>;
  }

  async RequestListConfigProjectionExecutionDataSourceQueries(projectionExecutionId:string, from: number, maxitems: number) : Promise<Array<ProjectionExecutionDataSourceQuery>>
  {
    let msg= {
      request: "list-config-projection-executions-datasourcequery",
      projectionexecutionid: projectionExecutionId,
      maxitems: maxitems,
      seekoffset: from,
      path: "/",
    };

    return (await this.Request(msg)) as Array<ProjectionExecutionDataSourceQuery>;
  }

  async RequestListConfigProjectionExecutionLuaMainFunctions(projectionExecutionId:string, from: number, maxitems: number) : Promise<Array<ProjectionExecutionLuaMainFunction>>
  {
    let msg= {
      request: "list-config-projection-executions-luamainfunction",
      projectionexecutionid: projectionExecutionId,
      maxitems: maxitems,
      seekoffset: from,
      path: "/",
    };

    return (await this.Request(msg)) as Array<ProjectionExecutionLuaMainFunction>;
  }


  async RequestGetConfigProjection(projectionId:string) : Promise<Projection>
  {
    let msg= {
      request: "get-config-projection",
      projectionid: projectionId,
      path: "/",
    };

    return (await this.Request(msg))[0] as Projection;
  }

  async RequestListConfigProjectionColumns(projectionId:string, from: number, maxitems: number) : Promise<Array<ProjectionColumnDescription>>
  {
    let msg= {
      request: "list-config-projection-columns",
      projectionid: projectionId,
      seekoffset: from,
      maxitems: maxitems,
      path: "/",
    };

    let result = (await this.Request(msg)) as Array<ProjectionColumnDescription>;
    return result.sort((a, b) => a.columnname.toLocaleLowerCase() > b.columnname.toLocaleLowerCase() ? 1 : -1);
  }

  async RequestSubscribeProjectionData(projectionId: string, group : string[] = []) : Promise<boolean>
  {
    let msg= {
      request: "subscribe-queryable-data",
      projectionid: projectionId,
      group: group.join(','),
      path: "/",
    };

    await this.Request(msg);
    return true;
  }

  async RequestSubscribeConfigProjection(projectionId: string) : Promise<boolean>
  {
    let msg= {
      request: "subscribe-config-projection",
      projectionid: projectionId,
      path: "/",
    };

    await this.Request(msg);
    return true;
  }
  async RequestSubscribeConfigDatasource(datasourceId: string) : Promise<boolean>
  {
    let msg= {
      request: "subscribe-config-datasource",
      datasourceid: datasourceId,
      path: "/",
    };

    await this.Request(msg);
    return true;
  }

  async RequestUnsubscribeProjectionData(projectionId: string, group : string[] = []) : Promise<boolean>
  {
    let msg= {
      request: "unsubscribe-queryable-data",
      projectionid: projectionId,
      group: group.join(','),
      path: "/",
    };

    await this.Request(msg);
    return true;
  }

  async RequestUnsubscribeConfigProjection(projectionId: string) : Promise<boolean>
  {
    let msg= {
      request: "unsubscribe-config-projection",
      projectionid: projectionId,
      path: "/",
    };

    await this.Request(msg);
    return true;
  }
  async RequestUnsubscribeConfigDatasource(datasourceId: string) : Promise<boolean>
  {
    let msg= {
      request: "unsubscribe-config-datasource",
      datasourceid: datasourceId,
      path: "/",
    };

    await this.Request(msg);
    return true;
  }
  async RequestRemoveConfigProjectionColumn(columnId: string) : Promise<boolean>
  {
    let msg= {
      request: "remove-config-projection-column",
      projectioncolumndescriptionid: columnId,
      path: "/",
    };

    await this.Request(msg);
    return true;
  }

  async RequestRunConfigProjection(projectionId: string) : Promise<ProjectionExecutionLog>
  {
    let msg= {
      request: "run-config-projection",
      projectionid: projectionId,
      path: "/",
    };

    return (await this.Request(msg))[0] as ProjectionExecutionLog;
  }

  async RequestResetConfigProjectionData(projectionId: string) : Promise<boolean>
  {
    let msg= {
      request: "reset-config-projection-data",
      projectionid: projectionId,
      path: "/",
    };

    await this.Request(msg);
    return true;
  }

  async RequestResetConfigProjectionGroupData(resetProjectionData : ResetProjectionDataQuery) : Promise<boolean>
  {
    let msg= {
      request: "reset-config-projection-group-data",
      data: resetProjectionData,

      path: "/",
    };

    await this.Request(msg);
    return true;
  }

  async RequestRemoveConfigProjection(projectionId: string) : Promise<boolean>
  {
    let msg= {
      request: "remove-config-projection",
      projectionid: projectionId,
      path: "/",
    };

    await this.Request(msg);
    return true;
  }

  async RequstRemoveConfigProjectionExecution(projectionexecutionid: string) : Promise<boolean>
  {
    let msg= {
      request: "remove-config-projection-execution",
      projectionexecutionid: projectionexecutionid,
      path: "/",
    };

    await this.Request(msg);
    return true;
  }
  async RequstRemoveConfigProjectionExecutionDataSourceQuery(projectionexecutiondatasourcequeryid: string) : Promise<boolean>
  {
    let msg= {
      request: "remove-config-projection-execution-datasourcequery",
      projectionexecutiondatasourcequeryid: projectionexecutiondatasourcequeryid,
      path: "/",
    };

    await this.Request(msg);
    return true;
  }
  async RequstRemoveConfigProjectionExecutionLuaMainFunction(projectionexecutionluamainfunctionid: string) : Promise<boolean>
  {
    let msg= {
      request: "remove-config-projection-execution-luamainfunction",
      projectionexecutionluamainfunctionid: projectionexecutionluamainfunctionid,
      path: "/",
    };

    await this.Request(msg);
    return true;
  }

  async RequestSetData(query : SetDataQuery, sender : string = '', senderId : string = '') : Promise<boolean>
  {
    let msg= {
      request: "query-setdata",
      data: query,
      path: "/",
    };

    this.logger.debug('RequestSetData', 'sender: ' + sender, 'senderid: ' + senderId, query);

    let rval = await this.Request(msg);

    this.logger.debug('RequestSetData response', 'sender: ' + sender, 'senderid: ' + senderId, rval);

    return true;
  }

  async RequestSetSplittedData(query : SetSplittedDataQuery) : Promise<boolean>
  {
    let msg= {
      request: "query-setsplitteddata",
      data: query,
      path: "/",
    };

    await this.Request(msg);
    return true;
  }

  async RequestGetVersion() : Promise<VersionResponse>
  {
    let msg= {
      request: "get-version",
      path: "/",
    };

    let result = (await this.Request(msg)) as Array<VersionResponse>;
    return result[0];
  }

  async RequestGetBlob(blobid : string) : Promise<Blob>
  {
    let msg= {
      request: "blobs-get-blob",
      blobid: blobid,
      path: "/",
    };

    let result = (await this.Request(msg)) as Array<Blob>;
    return result[0];
  }

  async RequestGetBlobIDs(from : number, maxitems : number) : Promise<Array<string>>
  {
    let msg= {
      request: "blobs-list-blobidentifiers",
      path: "/",
    };

    let result = (await this.Request(msg) as unknown) as Array<string>;
    return result;
  }


  async RequestSaveBlob(blob : SetBlob) : Promise<boolean>
  {
    let msg= {
      request: "blobs-save-blob",
      data: blob,
      path: "/",
    };

    await this.Request(msg);
    return true;
  }

  async RequestRemoveBlob(blobid : string) : Promise<boolean>
  {
    let msg= {
      request: "blobs-remove-blob",
      blobid: blobid,
      path: "/",
    };

    await this.Request(msg);
    return true;
  }

  async LuaDebuggerAttach(SID: string): Promise<boolean>
  {
    let command = new LuaDebuggerCommandAttach();
    command.sid = SID;
    let msg= {
      request: "scripts-lua-debugger-attach",
      data: command,
      path: "/",
    };

    await this.Request(msg);
    return true;
  }

  async LuaDebuggerRun(SID: string): Promise<boolean>
  {
    let command = new LuaDebuggerCommandRun();
    command.sid = SID;
    let msg= {
      request: "scripts-lua-debugger-run",
      data: command,
      path: "/",
    };

    await this.Request(msg);
    return true;
  }

  async LuaDebuggerRelease(SID: string): Promise<boolean>
  {
    let command = new LuaDebuggerCommandRelease();
    command.sid = SID;
    let msg= {
      request: "scripts-lua-debugger-release",
      data: command,
      path: "/",
    };

    await this.Request(msg);
    return true;
  }

  async LuaDebuggerReleaseAll(): Promise<boolean>
  {
    let msg= {
      request: "scripts-lua-debugger-release-all",
      path: "/",
    };

    await this.Request(msg);
    return true;
  }

  async LuaDebuggerStepInto(SID: string): Promise<boolean>
  {
    let command = new LuaDebuggerCommandStepInto();
    command.sid = SID;
    let msg= {
      request: "scripts-lua-debugger-step-into",
      data: command,
      path: "/",
    };

    await this.Request(msg);
    return true;
  }

  async LuaDebuggerStepOver(SID: string): Promise<boolean>
  {
    let command = new LuaDebuggerCommandStepOver();
    command.sid = SID;
    let msg= {
      request: "scripts-lua-debugger-step-over",
      data: command,
      path: "/",
    };

    await this.Request(msg);
    return true;
  }

  async LuaDebuggerSetBreakpoints(SID: string, lines: number[]): Promise<boolean>
  {
    let command = new LuaDebuggerCommandSetBreakpoints();
    command.lines = lines;
    command.sid = SID;
    let msg= {
      request: "scripts-lua-debugger-set-breakpoints",
      data: command,
      path: "/",
    };

    await this.Request(msg);
    return true;
  }

  async LuaDebuggerGetValues(SID: string, Instances: string[]): Promise<LuaDebuggerCommandGetValuesResponse>
  {
    let command = new LuaDebuggerCommandGetValues();

    command.sid = SID;
    command.instances = Instances;
    let msg= {
      request: "scripts-lua-debugger-get-values",
      data: command,
      path: "/",
    };

    return (await this.Request(msg))[0] as LuaDebuggerCommandGetValuesResponse;
  }

  async LuaDebuggerGetSource(SID: string): Promise<LuaDebuggerCommandGetSourceResponse>
  {
    let command = new LuaDebuggerCommandGetSource();
    command.sid = SID;
    let msg= {
      request: "scripts-lua-debugger-get-source",
      data: command,
      path: "/",
    };

    return (await this.Request(msg))[0] as LuaDebuggerCommandGetSourceResponse;
  }

  async LuaDebuggerGetStatus(SID: string): Promise<LuaDebuggerCommandGetStatusResponse>
  {
    let command = new LuaDebuggerCommandGetStatusResponse();

    command.sid = SID;
    let msg= {
      request: "scripts-lua-debugger-get-status",
      data: command,
      path: "/",
    };

    return (await this.Request(msg))[0] as LuaDebuggerCommandGetStatusResponse;
  }


  async LuaDebuggerListPotentialIdentifiers(seekoffset: number, maxitems: number): Promise<Array<string>>
  {
    let msg= {
      request: "scripts-lua-debugger-list-identifiers",
      seekoffset: seekoffset,
      maxitems: maxitems,
      path: "/",
    };

    return ((await this.Request(msg)) as unknown) as Array<string>;
  }

  async delay(ms: number) : Promise<any>
  {
      return new Promise( resolve => setTimeout(resolve, ms) );
  }

  async SendMessage(data : Object) {
    this.client.SendMessage(data);
  }

  GetNrOfPoints(queryResult : BaseQueryResult) : number {
    let result = queryResult.nrpoints;
    if (queryResult.datanumbers.length > 0) {
      result = queryResult.datanumbers[0].length;
    }
    else if (queryResult.datastrings.length > 0) {
      result = queryResult.datastrings[0].length;
    }
    else if (queryResult.datatimestamps.length > 0) {
      result = queryResult.datatimestamps[0].length;
    }

    return result;
  }

  private async RequestWithCache(msg: any, forcereload : boolean = false) : Promise<Array<object>>
  {
    if (this.settings.cacheEnabled) {
      let key = JSON.stringify(msg);

      if (!forcereload && this.cache.has(key)) {
        this.logger.info('request: returned cached value');
        return Promise.resolve(this.cache.get(key));
      }
      else {
        let result = await this.Request(msg);
        this.cache.set(key, result);
        let that = this;
        setTimeout(() => {
          try {
            that.cache.get(key);
          }
          catch {}
          }, (this.settings.cacheTTLSeconds + 10)*1000);
        return Promise.resolve(result);
      }
    }
    else {
      return this.Request(msg);
    }
  }

  private async Request(msg: any) : Promise<Array<object>>
  {
    let nrRetries = 500;
    for(let i = 0; i < nrRetries; i++)
    {
      if(this.client.isAuthenticated)
        break;

      if(i == 0 )
      {
        this.logger.info("request: waiting for connection");
      }
      //this.logger.info("request: waiting for connection");
      await this.delay(50);
    }

    if(!this.client.isAuthenticated)
    {
      this.logger.info("request: still not authenticated");
      throw new Error("Not connected");
    }



    let Resolves = this.ActiveResponsesResolve;
    let that = this;
    let RVal = new Promise< Array<Object>> (
      function (resolve, reject)
      {
        let msgid = that.client.GetNextMessageID();
        msg["messageid"] = msgid;
        Resolves.set(msgid, {resolve: resolve, reject: reject});
        that.client.SendMessageIgnoreID(msg);
      });

      return RVal;
  }

    // XAUTO
    async XAUTO_SaveProcessGraph( graph: XAUTO_XAutoProcessGraph ) : Promise<boolean>
    {
      let msg= {
        request: "xauto-save-processgraph",
        data: graph
      };

      await this.Request(msg);
      return true;
    }
    async XAUTO_GetProcessGraphs( seekoffset : number, maxitems : number) : Promise<Array<XAUTO_XAutoProcessGraph>>
    {
      let msg= {
        request: "xauto-list-processgraphs",
        seekoffset: seekoffset,
        maxitems: maxitems
      };

      let result = (await this.Request(msg)) as Array<XAUTO_XAutoProcessGraph>;
      return result;
    }

    async XAUTO_GetProcessGraph( xautoprocessgraphid: string ) : Promise<XAUTO_XAutoProcessGraph>
    {
      let msg= {
        request: "xauto-get-processgraph",
        xautoprocessgraphid: xautoprocessgraphid
      };

      let result = ((await this.Request(msg)) as Array<XAUTO_XAutoProcessGraph>)[0];
      return result;
    }

    async XAUTO_RemoveProcessGraph( xautoprocessgraphid: string ) : Promise<boolean>
    {
      let msg= {
        request: "xauto-remove-processgraph",
        xautoprocessgraphid: xautoprocessgraphid
      };

      await this.Request(msg);
      return true;
    }

    async XAUTO_GetProcessGraphSVG( xautoprocessgraphid: string ) : Promise<string>
    {
      let msg= {
        request: "xauto-get-processgraph-svg",
        xautoprocessgraphid: xautoprocessgraphid
      };

      let result = ((await this.Request(msg) as unknown) as Array<string>)[0];
      return result;
    }
    async XAUTO_SaveProcess3DGraph( graph: XAUTO_XAutoProcess3DGraph ) : Promise<boolean>
    {
      let msg= {
        request: "xauto-save-process3dgraph",
        data: graph
      };

      await this.Request(msg);
      return true;
    }
    async XAUTO_GetProcess3DGraphs( seekoffset : number, maxitems : number) : Promise<Array<XAUTO_XAutoProcess3DGraph>>
    {
      let msg= {
        request: "xauto-list-process3dgraphs",
        seekoffset: seekoffset,
        maxitems: maxitems
      };

      let result = (await this.Request(msg)) as Array<XAUTO_XAutoProcess3DGraph>;
      return result;
    }

    async XAUTO_GetProcess3DGraph( xautoprocess3dgraphid: string ) : Promise<XAUTO_XAutoProcess3DGraph>
    {
      let msg= {
        request: "xauto-get-process3Dgraph",
        xautoprocess3dgraphid: xautoprocess3dgraphid
      };

      let result = ((await this.Request(msg)) as Array<XAUTO_XAutoProcess3DGraph>)[0];
      return result;
    }

    async XAUTO_RemoveProcess3DGraph( xautoprocess3dgraphid: string ) : Promise<boolean>
    {
      let msg= {
        request: "xauto-remove-process3dgraph",
        xautoprocess3dgraphid: xautoprocess3dgraphid
      };

      await this.Request(msg);
      return true;
    }

    async XAUTO_GetProcessGraphBabylon( xautoprocess3dgraphid: string ) : Promise<string>
    {
      let msg= {
        request: "xauto-get-processgraph-babylon",
        xautoprocess3dgraphid: xautoprocess3dgraphid
      };

      let result = ((await this.Request(msg) as unknown) as Array<string>)[0];
      return result;
    }

    async XAUTO_SaveProgram( program: XAUTO_XAutoProgram ) : Promise<boolean>
    {
      let msg= {
        request: "xauto-save-program",
        data: program
      };

      await this.Request(msg);
      return true;
    }
    async XAUTO_SaveProgramLua( program: XAUTO_XAutoProgramLua ) : Promise<boolean>
    {
      let msg= {
        request: "xauto-save-program-lua",
        data: program
      };

      await this.Request(msg);
      return true;
    }
    async XAUTO_ExecuteLuaProgram( program: XAUTO_XAutoProgram, programlua: XAUTO_XAutoProgramLua ) : Promise<string>
    {
      let msg= {
        request: "xauto-execute-program-lua",
        data: {"program": program, "programlua": programlua}
      };

      let result = ((await this.Request(msg)) as Array<unknown>) as Array<string>;
      return result[0];
    }
    async XAUTO_GetPrograms( seekoffset, maxitems ) : Promise<Array<XAUTO_XAutoProgram>>
    {
      let msg= {
        request: "xauto-list-programs",
        seekoffset: seekoffset,
        maxitems: maxitems
      };

      let result = (await this.Request(msg)) as Array<XAUTO_XAutoProgram>;
      return result;
    }

    async XAUTO_GetProgram( xautoprogramid: string ) : Promise<XAUTO_XAutoProgram>
    {
      let msg= {
        request: "xauto-get-program",
        xautoprogramid: xautoprogramid
      };

      let result = ((await this.Request(msg)) as Array<XAUTO_XAutoProgram>)[0];
      return result;
    }

    async XAUTO_GetProgramLua( xautoprogramid: string ) : Promise<XAUTO_XAutoProgramLua>
    {
      let msg= {
        request: "xauto-get-program-lua",
        xautoprogramid: xautoprogramid
      };

      let result = ((await this.Request(msg)) as Array<XAUTO_XAutoProgramLua>)[0];
      return result;
    }

    async XAUTO_SaveProgramsScheduling( program: XAUTO_XAutoProgramScheduling ) : Promise<boolean>
    {
      let msg= {
        request: "xauto-save-programsscheduling",
        data: program
      };

      await this.Request(msg);
      return true;
    }
    async XAUTO_SaveProgramsSchedulingInterval( program: XAUTO_XAutoProgramSchedulingInterval ) : Promise<boolean>
    {
      let msg= {
        request: "xauto-save-programsscheduling-interval",
        data: program
      };

      await this.Request(msg);
      return true;
    }

    async XAUTO_GetProgramsSchedulings( seekoffset, maxitems ) : Promise<Array<XAUTO_XAutoProgramScheduling>>
    {
      let msg= {
        request: "xauto-list-programsschedulings",
        seekoffset: seekoffset,
        maxitems: maxitems
      };

      let result = (await this.Request(msg)) as Array<XAUTO_XAutoProgramScheduling>;
      return result;
    }

    async XAUTO_GetProgramsScheduling( xautoprogramschedulingid: string ) : Promise<XAUTO_XAutoProgramScheduling>
    {
      let msg= {
        request: "xauto-get-programsscheduling",
        xautoprogramschedulingid: xautoprogramschedulingid
      };

      let result = ((await this.Request(msg)) as Array<XAUTO_XAutoProgramScheduling>)[0];
      return result;
    }

    async XAUTO_GetProgramsSchedulingInterval( xautoprogramschedulingid: string ) : Promise<XAUTO_XAutoProgramSchedulingInterval>
    {
      let msg= {
        request: "xauto-get-programsscheduling-interval",
        xautoprogramschedulingid: xautoprogramschedulingid
      };

      let result = ((await this.Request(msg)) as Array<XAUTO_XAutoProgramSchedulingInterval>)[0];
      return result;
    }

    async XAUTO_RemoveProgramsScheduling( xautoprogramschedulingid: string ) : Promise<boolean>
    {
      let msg= {
        request: "xauto-remove-programsscheduling",
        xautoprogramschedulingid: xautoprogramschedulingid
      };

      await this.Request(msg);
      return true;
    }

    async XAUTO_SaveDriver( driver: XAUTO_XAutoDriver ) : Promise<boolean>
    {
      let msg= {
        request: "xauto-save-driver",
        data: driver
      };

      await this.Request(msg);
      return true;
    }
    async XAUTO_SaveVariable( variable: XAUTO_XAutoVariable ) : Promise<boolean>
    {
      let msg= {
        request: "xauto-save-variable",
        data: variable
      };

      await this.Request(msg);
      return true;
    }

    async XAUTO_GetDrivers( seekoffset, maxitems ) : Promise<Array<XAUTO_XAutoDriver>>
    {
      let msg= {
        request: "xauto-list-drivers",
        seekoffset: seekoffset,
        maxitems: maxitems
      };

      let result = (await this.Request(msg)) as Array<XAUTO_XAutoDriver>;
      return result;
    }

    async XAUTO_GetVariables( seekoffset, maxitems, xautodriverid="" ) : Promise<Array<XAUTO_XAutoVariable>>
    {
      let msg= {
        request: "xauto-list-variables",
        xautodriverid: xautodriverid,
        seekoffset: seekoffset,
        maxitems: maxitems
      };

      let result = (await this.Request(msg)) as Array<XAUTO_XAutoVariable>;
      return result;
    }


    // XAUTO MODBUS
    async XAUTO_SaveDriverModbus( driver: XAUTO_XAutoDriverModbus ) : Promise<boolean>
    {
      let msg= {
        request: "xauto-save-driver-modbus",
        data: driver
      };

      await this.Request(msg);
      return true;
    }
    async XAUTO_SaveVariableModbus( variable: XAUTO_XAutoVariableModbus ) : Promise<boolean>
    {
      let msg= {
        request: "xauto-save-variable-modbus",
        data: variable
      };

      await this.Request(msg);
      return true;
    }
    async XAUTO_GetDriverModbus( xautodriverid: string ) : Promise<XAUTO_XAutoDriverModbus>
    {
      let msg= {
        request: "xauto-get-driver-modbus",
        xautodriverid: xautodriverid
      };

      let result = ((await this.Request(msg)) as Array<XAUTO_XAutoDriverModbus>)[0];
      return result;
    }
    async XAUTO_GetVariableModbus( xautovariableid: string ) : Promise<XAUTO_XAutoVariableModbus>
    {
      let msg= {
        request: "xauto-get-variable-modbus",
        xautovariableid: xautovariableid
      };

      let result = ((await this.Request(msg)) as Array<XAUTO_XAutoVariableModbus>)[0];
      return result;
    }

    // XAUTO MQTT
    async XAUTO_SaveDriverMQTT( driver: XAUTO_XAutoDriverMqtt ) : Promise<boolean>
    {
      let msg= {
        request: "xauto-save-driver-mqtt",
        data: driver
      };

      await this.Request(msg);
      return true;
    }
    async XAUTO_SaveVariableMQTT( variable: XAUTO_XAutoVariableMqtt ) : Promise<boolean>
    {
      let msg= {
        request: "xauto-save-variable-mqtt",
        data: variable
      };

      await this.Request(msg);
      return true;
    }
    async XAUTO_GetDriverMqtt( xautodriverid: string ) : Promise<XAUTO_XAutoDriverMqtt>
    {
      let msg= {
        request: "xauto-get-driver-mqtt",
        xautodriverid: xautodriverid
      };

      let result = ((await this.Request(msg)) as Array<XAUTO_XAutoDriverMqtt>)[0];
      return result;
    }
    async XAUTO_GetVariableMqtt( xautovariableid: string ) : Promise<XAUTO_XAutoVariableMqtt>
    {
      let msg= {
        request: "xauto-get-variable-mqtt",
        xautovariableid: xautovariableid
      };

      let result = ((await this.Request(msg)) as Array<XAUTO_XAutoVariableMqtt>)[0];
      return result;
    }

     // XAUTO External
     async XAUTO_SaveDriverExternal( driver: XAUTO_XAutoDriverExternal ) : Promise<boolean>
     {
       let msg= {
         request: "xauto-save-driver-external",
         data: driver
       };

       await this.Request(msg);
       return true;
     }
     async XAUTO_SaveVariableExternal( variable: XAUTO_XAutoVariableExternal ) : Promise<boolean>
     {
       let msg= {
         request: "xauto-save-variable-external",
         data: variable
       };

       await this.Request(msg);
       return true;
     }
     async XAUTO_GetDriverExternal( xautodriverid: string ) : Promise<XAUTO_XAutoDriverExternal>
     {
       let msg= {
         request: "xauto-get-driver-external",
         xautodriverid: xautodriverid
       };

       let result = ((await this.Request(msg)) as Array<XAUTO_XAutoDriverExternal>)[0];
       return result;
     }
     async XAUTO_GetVariableExternal( xautovariableid: string ) : Promise<XAUTO_XAutoVariableExternal>
     {
       let msg= {
         request: "xauto-get-variable-external",
         xautovariableid: xautovariableid
       };

       let result = ((await this.Request(msg)) as Array<XAUTO_XAutoVariableExternal>)[0];
       return result;
     }

    // XAUTO GPIO
    async XAUTO_SaveDriverGpio( driver: XAUTO_XAutoDriverGpio ) : Promise<boolean>
    {
      let msg= {
        request: "xauto-save-driver-gpio",
        data: driver
      };

      await this.Request(msg);
      return true;
    }
    async XAUTO_SaveVariableGpio( variable: XAUTO_XAutoVariableGpio ) : Promise<boolean>
    {
      let msg= {
        request: "xauto-save-variable-gpio",
        data: variable
      };

      await this.Request(msg);
      return true;
    }
    async XAUTO_GetDriverGpio( xautodriverid: string ) : Promise<XAUTO_XAutoDriverGpio>
    {
      let msg= {
        request: "xauto-get-driver-gpio",
        xautodriverid: xautodriverid
      };

      let result = ((await this.Request(msg)) as Array<XAUTO_XAutoDriverGpio>)[0];
      return result;
    }
    async XAUTO_GetVariableGpio( xautovariableid: string ) : Promise<XAUTO_XAutoVariableGpio>
    {
      let msg= {
        request: "xauto-get-variable-gpio",
        xautovariableid: xautovariableid
      };

      let result = ((await this.Request(msg)) as Array<XAUTO_XAutoVariableGpio>)[0];
      return result;
    }

    // XAUTO Redis
    async XAUTO_SaveDriverRedis( driver: XAUTO_XAutoDriverRedis ) : Promise<boolean>
    {
      let msg= {
        request: "xauto-save-driver-redis",
        data: driver
      };

      await this.Request(msg);
      return true;
    }
    async XAUTO_SaveVariableRedis( variable: XAUTO_XAutoVariableRedis ) : Promise<boolean>
    {
      let msg= {
        request: "xauto-save-variable-redis",
        data: variable
      };

      await this.Request(msg);
      return true;
    }
    async XAUTO_GetDriverRedis( xautodriverid: string ) : Promise<XAUTO_XAutoDriverRedis>
    {
      let msg= {
        request: "xauto-get-driver-redis",
        xautodriverid: xautodriverid
      };

      let result = ((await this.Request(msg)) as Array<XAUTO_XAutoDriverRedis>)[0];
      return result;
    }
    async XAUTO_GetVariableRedis( xautovariableid: string ) : Promise<XAUTO_XAutoVariableRedis>
    {
      let msg= {
        request: "xauto-get-variable-redis",
        xautovariableid: xautovariableid
      };

      let result = ((await this.Request(msg)) as Array<XAUTO_XAutoVariableRedis>)[0];
      return result;
    }
    // XAUTO COLLECTD
    async XAUTO_SaveDriverCollectd( driver: XAUTO_XAutoDriverCollectd ) : Promise<boolean>
    {
      let msg= {
        request: "xauto-save-driver-collectd",
        data: driver
      };

      await this.Request(msg);
      return true;
    }
    async XAUTO_SaveVariableCollectd( variable: XAUTO_XAutoVariableCollectd ) : Promise<boolean>
    {
      let msg= {
        request: "xauto-save-variable-collectd",
        data: variable
      };

      await this.Request(msg);
      return true;
    }
    async XAUTO_GetDriverCollectd( xautodriverid: string ) : Promise<XAUTO_XAutoDriverCollectd>
    {
      let msg= {
        request: "xauto-get-driver-collectd",
        xautodriverid: xautodriverid
      };

      let result = ((await this.Request(msg)) as Array<XAUTO_XAutoDriverCollectd>)[0];
      return result;
    }
    async XAUTO_GetVariableCollectd( xautovariableid: string ) : Promise<XAUTO_XAutoVariableCollectd>
    {
      let msg= {
        request: "xauto-get-variable-collectd",
        xautovariableid: xautovariableid
      };

      let result = ((await this.Request(msg)) as Array<XAUTO_XAutoVariableCollectd>)[0];
      return result;
    }
    async XAUTO_HelperProvisionSignalsCollectd( xautodriverid: string ) : Promise<boolean>
    {
      let msg= {
        request: "xauto-helper-collectd-provisionsignals",
        xautodriverid: xautodriverid
      };

      let result = await this.Request(msg);
      return result[0] as unknown as boolean;
    }

    // XAUTO BACNET
    async XAUTO_SaveDriverBacnet( driver: XAUTO_XAutoDriverBacnet ) : Promise<boolean>
    {
      let msg= {
        request: "xauto-save-driver-bacnet",
        data: driver
      };

      await this.Request(msg);
      return true;
    }
    async XAUTO_SaveVariableBacnet( variable: XAUTO_XAutoVariableBacnet ) : Promise<boolean>
    {
      let msg= {
        request: "xauto-save-variable-bacnet",
        data: variable
      };

      await this.Request(msg);
      return true;
    }
    async XAUTO_GetDriverBacnet( xautodriverid: string ) : Promise<XAUTO_XAutoDriverBacnet>
    {
      let msg= {
        request: "xauto-get-driver-bacnet",
        xautodriverid: xautodriverid
      };

      let result = ((await this.Request(msg)) as Array<XAUTO_XAutoDriverBacnet>)[0];
      return result;
    }
    async XAUTO_GetVariableBacnet( xautovariableid: string ) : Promise<XAUTO_XAutoVariableBacnet>
    {
      let msg= {
        request: "xauto-get-variable-bacnet",
        xautovariableid: xautovariableid
      };

      let result = ((await this.Request(msg)) as Array<XAUTO_XAutoVariableBacnet>)[0];
      return result;
    }
    async XAUTO_GetVariablesDiscoveredBacnet( seekoffset, maxitems, xautodriverid="", minmodifiedat = new Date(1970,1,1) ) : Promise<Array<XAUTO_XAutoVariableWithBacnet>>
    {
      let msg= {
        request: "xauto-list-discovered-variables-bacnet",
        data: 
        {
          xautodriverid: xautodriverid,
          seekoffset: seekoffset,
          maxitems: maxitems,
          minmodifiedat: minmodifiedat
        }
      };

      let result = (await this.Request(msg)) as Array<XAUTO_XAutoVariableWithBacnet>;
      return result;
    }

    // XAUTO OPCUA
    async XAUTO_SaveDriverOpcua( driver: XAUTO_XAutoDriverOpcua ) : Promise<boolean>
    {
      let msg= {
        request: "xauto-save-driver-opcua",
        data: driver
      };

      await this.Request(msg);
      return true;
    }
    async XAUTO_SaveVariableOpcua( variable: XAUTO_XAutoVariableOpcua ) : Promise<boolean>
    {
      let msg= {
        request: "xauto-save-variable-opcua",
        data: variable
      };

      await this.Request(msg);
      return true;
    }
    async XAUTO_GetDriverOpcua( xautodriverid: string ) : Promise<XAUTO_XAutoDriverOpcua>
    {
      let msg= {
        request: "xauto-get-driver-opcua",
        xautodriverid: xautodriverid
      };

      let result = ((await this.Request(msg)) as Array<XAUTO_XAutoDriverOpcua>)[0];
      return result;
    }
    async XAUTO_GetVariableOpcua( xautovariableid: string ) : Promise<XAUTO_XAutoVariableOpcua>
    {
      let msg= {
        request: "xauto-get-variable-opcua",
        xautovariableid: xautovariableid
      };

      let result = ((await this.Request(msg)) as Array<XAUTO_XAutoVariableOpcua>)[0];
      return result;
    }
    async XAUTO_HelperProvisionSignalsOpcua( xautodriverid: string ) : Promise<boolean>
    {
      let msg= {
        request: "xauto-helper-opcua-provisionsignals",
        xautodriverid: xautodriverid
      };

      let result = await this.Request(msg);
      return result[0] as unknown as boolean;
    }

    // XAUTO MIDI
    async XAUTO_SaveDriverMidi( driver: XAUTO_XAutoDriverMidi ) : Promise<boolean>
    {
      let msg= {
        request: "xauto-save-driver-midi",
        data: driver
      };

      await this.Request(msg);
      return true;
    }
    async XAUTO_SaveVariableMidi( variable: XAUTO_XAutoVariableMidi ) : Promise<boolean>
    {
      let msg= {
        request: "xauto-save-variable-midi",
        data: variable
      };

      await this.Request(msg);
      return true;
    }
    async XAUTO_GetDriverMidi( xautodriverid: string ) : Promise<XAUTO_XAutoDriverMidi>
    {
      let msg= {
        request: "xauto-get-driver-midi",
        xautodriverid: xautodriverid
      };

      let result = ((await this.Request(msg)) as Array<XAUTO_XAutoDriverMidi>)[0];
      return result;
    }
    async XAUTO_GetVariableMidi( xautovariableid: string ) : Promise<XAUTO_XAutoVariableMidi>
    {
      let msg= {
        request: "xauto-get-variable-midi",
        xautovariableid: xautovariableid
      };

      let result = ((await this.Request(msg)) as Array<XAUTO_XAutoVariableMidi>)[0];
      return result;
    }


    // XAUTO MBUS
    async XAUTO_SaveDriverMbus( driver: XAUTO_XAutoDriverMbus ) : Promise<boolean>
    {
      let msg= {
        request: "xauto-save-driver-mbus",
        data: driver
      };

      await this.Request(msg);
      return true;
    }
    async XAUTO_SaveVariableMbus( variable: XAUTO_XAutoVariableMbus ) : Promise<boolean>
    {
      let msg= {
        request: "xauto-save-variable-mbus",
        data: variable
      };

      await this.Request(msg);
      return true;
    }
    async XAUTO_GetDriverMbus( xautodriverid: string ) : Promise<XAUTO_XAutoDriverMbus>
    {
      let msg= {
        request: "xauto-get-driver-mbus",
        xautodriverid: xautodriverid
      };

      let result = ((await this.Request(msg)) as Array<XAUTO_XAutoDriverMbus>)[0];
      return result;
    }
    async XAUTO_GetVariableMbus( xautovariableid: string ) : Promise<XAUTO_XAutoVariableMbus>
    {
      let msg= {
        request: "xauto-get-variable-mbus",
        xautovariableid: xautovariableid
      };

      let result = ((await this.Request(msg)) as Array<XAUTO_XAutoVariableMbus>)[0];
      return result;
    }

    // XAUTO INTEGRATION SERVERS

    async XAUTO_GetIntegrationServers( seekoffset, maxitems ) : Promise<Array<XAUTO_XAutoIntegrationServer>>
    {
      let msg= {
        request: "xauto-list-integrationservers",
        seekoffset: seekoffset,
        maxitems: maxitems
      };

      let result = (await this.Request(msg)) as Array<XAUTO_XAutoIntegrationServer>;
      return result;
    }

    async XAUTO_SaveIntegrationServer( server: XAUTO_XAutoIntegrationServer ) : Promise<boolean>
    {
      let msg= {
        request: "xauto-save-integrationserver",
        data: server
      };

      await this.Request(msg);
      return true;
    }
    async XAUTO_RemoveIntegrationServer( serverid: string ) : Promise<boolean>
    {
      let msg= {
        request: "xauto-remove-integrationserver",
        xautointegrationserverid: serverid
      };

      await this.Request(msg);
      return true;
    }
    async XAUTO_SaveIntegrationServerMQTT( server: XAUTO_IntegrationServerMqtt ) : Promise<boolean>
    {
      let msg= {
        request: "xauto-save-integrationserver-mqtt",
        data: server
      };

      await this.Request(msg);
      return true;
    }
    async XAUTO_SaveIntegrationServerMQTTVariableMapping( mapping: XAUTO_IntegrationServerMqttVariableMapping ) : Promise<boolean>
    {
      let msg= {
        request: "xauto-save-integrationserver-mqtt-variablemapping",
        data: mapping
      };

      await this.Request(msg);
      return true;
    }




}
